import { StrictMode, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { createRoot } from "react-dom/client";
import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import "../../../packages/ui/src/product.css";
import { formatLocalizedCurrency, message as localizedMessage, type PortabilityMessageKey, type RequiredLocaleCode } from "../../../packages/ui/src/localization.js";
import { apiRequest, configureGooglePlaySandboxCertificationIdentity, qualificationAccountFromLocation } from "../../../packages/ui/src/runtime-client.js";
import {
  deleteEncryptedOfflineTrip,
  listEncryptedOfflineTrips,
  loadEncryptedOfflineTrip,
  queueOfflineTripMutation,
  saveEncryptedOfflineTrip,
  syncEncryptedOfflineTrips,
  type OfflineTripEssentialsPayload,
  type OfflineTripSummary
} from "../../../packages/ui/src/offline-trip-cache.js";
import { deriveExploreSearchCategories, resolveExploreSearchAvailability, resolveExploreSearchCategory } from "./explore.js";
import { ExploreDetail, ExploreFilters, ExploreGlobalResults, ExploreResults, type ExploreCard, type ExploreGlobalResult } from "./explore-surface.js";
import { readRecentSearches, readSavedTravelItems, recordRecentSearch, replaceExploreState, sortExploreOffers, toggleSavedTravelItem, type ExploreSort, type SavedTravelItem } from "./explore-state.js";
import { chooseAccountPreferences, type AccountExploreState, type CloudPreferencesEnvelope } from "./account-sync.js";
import { HomeExploreHelperPanel, type HomeExploreCapabilityInput, type HomeExplorePointsState, type HomeExploreSavedCounts, type HomeExploreTrip } from "./home/index.js";
import { ProfileSettings } from "./profile-settings.js";
import { applyMobilePreferences, loadMobilePreferences, saveMobilePreferences, type MobilePreferences, type PreferencesStorage } from "./preferences.js";
import { authenticateDeviceOwnerForSessionLock, openNativeAppSettings, readNativeSecurityStatus, type NativeSecurityStatus } from "./security/native-security.js";
import { TripWorkspace, type AddTripItemInput, type AgenticTextResult, type CreateTripInput, type OfflineTripStatus, type PlannerAcknowledgementInput, type PlannerFormInput, type PlannerResultSummary, type TripItineraryItem, type TripShareState, type UpdateTripInput, type UpdateTripItemInput } from "./trips/index.js";
import { CommerceAccountSurface } from "./commerce-account.js";
import { CommunityAccountSurface } from "./community-account.js";
import { CreatorCommunityPanel, type CreatorDiscoveryMode } from "./community/creator.js";
import { AgenticTravelCompanionPanel, type AgenticVoiceCaptureState, type AgenticVoiceOutputState } from "./agentic/index.js";
import type { CreatorStreamSummary } from "./community/types.js";
import type { DiscoveryCategory } from "../../../packages/domain/src/index.js";
import type { ProviderOffer } from "../../../packages/providers/src/index.js";
import {
  MOBILE_STORE_SUBSCRIPTION_IDS,
  deviceStorePlatform,
  finalizeMobileStoreConsumablePurchase,
  mobileStoreBillingAvailable,
  mobileStoreName,
  openMobileStoreSubscriptionManagement,
  purchaseMobileStoreMembership,
  purchaseMobileStoreProduct,
  queryMobileStoreMembershipProducts,
  queryMobileStoreOneTimeProducts,
  reconcileMobileStoreEntitlements,
  restoreMobileStorePurchases,
  watchMobileStorePurchaseUpdates,
  type MobileStoreProductDetails
} from "./mobile-billing.js";
import { authenticationCallbackErrorMessage } from "./auth-callback.js";
import { NATIVE_APPLICATION_ID, NATIVE_RELEASE_VERSION } from "./native-release.js";

if (Capacitor.getPlatform() === "android") {
  configureGooglePlaySandboxCertificationIdentity({
    applicationId: NATIVE_APPLICATION_ID,
    androidVersionCode: NATIVE_RELEASE_VERSION.androidVersionCode,
    track: "internal"
  });
}

type Tab = "home" | "explore" | "trips" | "live" | "profile";
type ProfileSection = "overview" | "account" | "membership" | "rewards" | "creator" | "family" | "preferences" | "support";

type MobileInlineTranslations = Readonly<Record<Exclude<RequiredLocaleCode, "en">, string>>;

const mobileInlineCatalog: Readonly<Record<string, MobileInlineTranslations>> = Object.freeze({
  "Your trips and offline essentials": {
    es: "Tus viajes y esenciales sin conexión",
    fr: "Vos voyages et essentiels hors ligne",
    de: "Deine Reisen und Offline-Essentials",
    it: "I tuoi viaggi e gli elementi essenziali offline",
    pt: "Suas viagens e essenciais offline",
    ja: "旅行とオフライン必需項目",
    ko: "내 여행 및 오프라인 필수 항목",
    "zh-Hans": "你的旅行和离线必备项",
    "zh-Hant": "你的旅行和離線必備項"
  },
  "Browse without an account. Sign in when cloud trips, bookings, or sync are needed.": {
    es: "Explora sin cuenta. Inicia sesión cuando necesites viajes en la nube, reservas o sincronización.",
    fr: "Explorez sans compte. Connectez-vous lorsque les voyages cloud, réservations ou synchronisation sont nécessaires.",
    de: "Stöbere ohne Konto. Melde dich an, wenn Cloud-Reisen, Buchungen oder Synchronisierung benötigt werden.",
    it: "Esplora senza account. Accedi quando servono viaggi cloud, prenotazioni o sincronizzazione.",
    pt: "Explore sem uma conta. Entre quando precisar de viagens na nuvem, reservas ou sincronização.",
    ja: "アカウントなしで閲覧できます。クラウド旅行、予約、同期が必要なときにサインインしてください。",
    ko: "계정 없이 둘러볼 수 있습니다. 클라우드 여행, 예약 또는 동기화가 필요할 때 로그인하세요.",
    "zh-Hans": "无需账户即可浏览。需要云端旅行、预订或同步时再登录。",
    "zh-Hant": "無需帳戶即可瀏覽。需要雲端旅行、預訂或同步時再登入。"
  },
  "Passwords are never created. Any encrypted offline essentials already saved on this device remain reachable without a network session.": {
    es: "Nunca se crean contraseñas. Los esenciales cifrados ya guardados en este dispositivo siguen disponibles sin una sesión de red.",
    fr: "Aucun mot de passe n’est créé. Les essentiels hors ligne chiffrés déjà enregistrés sur cet appareil restent accessibles sans session réseau.",
    de: "Passwörter werden nie erstellt. Verschlüsselte Offline-Essentials auf diesem Gerät bleiben ohne Netzwerksitzung erreichbar.",
    it: "Le password non vengono mai create. Gli elementi essenziali offline cifrati già salvati su questo dispositivo restano accessibili senza una sessione di rete.",
    pt: "Senhas nunca são criadas. Essenciais offline criptografados já salvos neste dispositivo continuam acessíveis sem uma sessão de rede.",
    ja: "パスワードは作成されません。このデバイスに保存済みの暗号化されたオフライン必需項目は、ネットワークセッションなしで引き続き利用できます。",
    ko: "비밀번호는 생성되지 않습니다. 이 기기에 이미 저장된 암호화된 오프라인 필수 항목은 네트워크 세션 없이도 접근할 수 있습니다.",
    "zh-Hans": "系统不会创建密码。已保存在此设备上的加密离线必备项无需网络会话仍可访问。",
    "zh-Hant": "系統不會建立密碼。已儲存在此裝置上的加密離線必備項無需網路工作階段仍可存取。"
  },
  "Cloud trips and sign-in will activate when Portability’s live service is connected. Nothing has been lost from this device.": {
    es: "Los viajes en la nube y el inicio de sesión se activarán cuando el servicio en vivo de Portability esté conectado. Nada se perdió de este dispositivo.",
    fr: "Les voyages cloud et la connexion s’activeront lorsque le service en direct de Portability sera connecté. Rien n’a été perdu sur cet appareil.",
    de: "Cloud-Reisen und Anmeldung werden aktiviert, sobald der Live-Dienst von Portability verbunden ist. Auf diesem Gerät ist nichts verloren gegangen.",
    it: "I viaggi cloud e l'accesso si attiveranno quando il servizio live di Portability sarà connesso. Nulla è andato perso da questo dispositivo.",
    pt: "Viagens na nuvem e login serão ativados quando o serviço ao vivo da Portability estiver conectado. Nada foi perdido neste dispositivo.",
    ja: "Portability のライブサービスが接続されると、クラウド旅行とサインインが有効になります。このデバイスから失われたものはありません。",
    ko: "Portability 라이브 서비스가 연결되면 클라우드 여행과 로그인이 활성화됩니다. 이 기기에서 손실된 항목은 없습니다.",
    "zh-Hans": "Portability 实时服务连接后，云端旅行和登录将启用。此设备上的内容没有丢失。",
    "zh-Hant": "Portability 即時服務連線後，雲端旅行和登入將啟用。此裝置上的內容沒有遺失。"
  },
  "OFFLINE": {
    es: "SIN CONEXIÓN",
    fr: "HORS LIGNE",
    de: "OFFLINE",
    it: "OFFLINE",
    pt: "OFFLINE",
    ja: "オフライン",
    ko: "오프라인",
    "zh-Hans": "离线",
    "zh-Hant": "離線"
  },
  "Read-only while offline. Live availability, purchases, changes, sharing, and cloud sync stay online-only.": {
    es: "Solo lectura sin conexión. Inventario en vivo, compras, cambios de proveedor, compartir y sincronización en la nube siguen solo en línea.",
    fr: "Lecture seule hors ligne. Inventaire en direct, achats, changements fournisseur, partage et synchronisation cloud restent en ligne uniquement.",
    de: "Offline nur lesbar. Live-Inventar, Käufe, Anbieteränderungen, Teilen und Cloud-Sync bleiben nur online verfügbar.",
    it: "Solo lettura offline. Inventario live, acquisti, modifiche dei provider, condivisione e sincronizzazione cloud restano solo online.",
    pt: "Somente leitura offline. Inventário ao vivo, compras, alterações de provedor, compartilhamento e sincronização na nuvem continuam somente online.",
    ja: "オフライン中は読み取り専用です。ライブ在庫、購入、プロバイダー変更、共有、クラウド同期はオンライン専用です。",
    ko: "오프라인에서는 읽기 전용입니다. 라이브 재고, 구매, 제공업체 변경, 공유 및 클라우드 동기화는 온라인에서만 가능합니다.",
    "zh-Hans": "离线时为只读。实时库存、购买、提供商变更、分享和云同步仍仅限在线使用。",
    "zh-Hant": "離線時為唯讀。即時庫存、購買、供應商變更、分享和雲端同步仍僅限線上使用。"
  },
  "Saved": {
    es: "Guardado",
    fr: "Enregistré",
    de: "Gespeichert",
    it: "Salvato",
    pt: "Salvo",
    ja: "保存済み",
    ko: "저장됨",
    "zh-Hans": "已保存",
    "zh-Hant": "已儲存"
  },
  "on this device": {
    es: "en este dispositivo",
    fr: "sur cet appareil",
    de: "auf diesem Gerät",
    it: "su questo dispositivo",
    pt: "neste dispositivo",
    ja: "このデバイス上",
    ko: "이 기기에",
    "zh-Hans": "在此设备上",
    "zh-Hant": "在此裝置上"
  },
  "Open offline": {
    es: "Abrir sin conexión",
    fr: "Ouvrir hors ligne",
    de: "Offline öffnen",
    it: "Apri offline",
    pt: "Abrir offline",
    ja: "オフラインで開く",
    ko: "오프라인으로 열기",
    "zh-Hans": "离线打开",
    "zh-Hant": "離線開啟"
  },
  "Delete offline": {
    es: "Eliminar sin conexión",
    fr: "Supprimer hors ligne",
    de: "Offline löschen",
    it: "Elimina offline",
    pt: "Excluir offline",
    ja: "オフライン項目を削除",
    ko: "오프라인 항목 삭제",
    "zh-Hans": "删除离线内容",
    "zh-Hant": "刪除離線內容"
  },
  "Your itinerary, one day at a time": {
    es: "Tu itinerario, un día a la vez",
    fr: "Votre itinéraire, un jour à la fois",
    de: "Dein Reiseplan, Tag für Tag",
    it: "Il tuo itinerario, un giorno alla volta",
    pt: "Seu itinerário, um dia de cada vez",
    ja: "1日ずつ進める旅程",
    ko: "하루씩 보는 내 일정",
    "zh-Hans": "你的行程，一天一步",
    "zh-Hant": "你的行程，一天一步"
  },
  "No separate Now screen. Current guidance stays here.": {
    es: "No hay pantalla Ahora separada. La guía actual permanece aquí.",
    fr: "Aucun écran Maintenant séparé. Les conseils actuels restent ici.",
    de: "Es gibt keinen separaten Jetzt-Bildschirm. Aktuelle Hinweise bleiben hier.",
    it: "Non c'è una schermata Ora separata. La guida attuale resta qui.",
    pt: "Não há uma tela Agora separada. A orientação atual fica aqui.",
    ja: "個別の「今」画面はありません。現在の案内はここに残ります。",
    ko: "별도의 지금 화면은 없습니다. 현재 안내는 여기에 유지됩니다.",
    "zh-Hans": "没有单独的“现在”屏幕。当前指引保留在这里。",
    "zh-Hant": "沒有單獨的「現在」畫面。目前指引保留在這裡。"
  },
  "Offline mode: encrypted essentials on this device remain readable.": {
    es: "Modo sin conexión: los esenciales cifrados de este dispositivo siguen legibles.",
    fr: "Mode hors ligne : les essentiels chiffrés sur cet appareil restent lisibles.",
    de: "Offline-Modus: Verschlüsselte Essentials auf diesem Gerät bleiben lesbar.",
    it: "Modalità offline: gli elementi essenziali cifrati su questo dispositivo restano leggibili.",
    pt: "Modo offline: essenciais criptografados neste dispositivo continuam legíveis.",
    ja: "オフラインモード: このデバイス上の暗号化された必需項目は引き続き読めます。",
    ko: "오프라인 모드: 이 기기의 암호화된 필수 항목은 계속 읽을 수 있습니다.",
    "zh-Hans": "离线模式：此设备上的加密必备项仍可读取。",
    "zh-Hant": "離線模式：此裝置上的加密必備項仍可讀取。"
  },
  "Encrypted essentials on this device": {
    es: "Esenciales cifrados en este dispositivo",
    fr: "Essentiels chiffrés sur cet appareil",
    de: "Verschlüsselte Essentials auf diesem Gerät",
    it: "Elementi essenziali cifrati su questo dispositivo",
    pt: "Essenciais criptografados neste dispositivo",
    ja: "このデバイス上の暗号化された必需項目",
    ko: "이 기기의 암호화된 필수 항목",
    "zh-Hans": "此设备上的加密必备项",
    "zh-Hant": "此裝置上的加密必備項"
  },
  "to": {
    es: "a",
    fr: "à",
    de: "bis",
    it: "a",
    pt: "a",
    ja: "から",
    ko: "부터",
    "zh-Hans": "到",
    "zh-Hant": "到"
  },
  "from": {
    es: "desde",
    fr: "de",
    de: "von",
    it: "da",
    pt: "de",
    ja: "から",
    ko: "부터",
    "zh-Hans": "从",
    "zh-Hant": "從"
  },
  "Sync state": {
    es: "Estado de sincronización",
    fr: "État de synchronisation",
    de: "Synchronisierungsstatus",
    it: "Stato sincronizzazione",
    pt: "Estado de sincronização",
    ja: "同期状態",
    ko: "동기화 상태",
    "zh-Hans": "同步状态",
    "zh-Hant": "同步狀態"
  }
});

function mobileInlineCopy(locale: RequiredLocaleCode, english: string, translations: Partial<Record<Exclude<RequiredLocaleCode, "en">, string>>): string {
  if (locale === "en") return english;
  const localeCopy = translations[locale] ?? mobileInlineCatalog[english]?.[locale];
  if (localeCopy === undefined) throw new Error(`Missing mobile inline copy for ${locale}: ${english}`);
  return localeCopy;
}

interface Account {
  readonly accountId: string;
  readonly displayName: string;
  readonly tier: "FREE" | "INSIDER" | "CREATOR" | "LIFETIME_CREATOR";
  readonly roles: readonly string[];
}

interface Entitlement {
  readonly tier: Account["tier"];
  readonly fullAgentic: true;
  readonly fxFeeBasisPoints: number;
  readonly kidsCornerIncludedDownloadsPerMonth: number;
  readonly hostLivestreams: boolean;
  readonly minimumPayoutMinorUsd: number | null;
}

interface Bootstrap {
  readonly navigation: readonly { readonly id: Tab; readonly label: string }[];
  readonly account: Account | null;
  readonly entitlement: Entitlement | null;
  readonly mobileStoreAccountToken: string | null;
  readonly tripCount: number;
  readonly providerCapabilities: readonly { readonly category: string; readonly state: string; readonly actionable: boolean; readonly reason: string; readonly providers: readonly string[] }[];
  readonly productionFixturesVisible: false;
}

function mobileBillingProvider(capabilities: Bootstrap["providerCapabilities"]): "storekit2" | "google_play_billing" | null {
  const platform = deviceStorePlatform();
  const provider = platform === "APPLE" ? "storekit2" : platform === "GOOGLE" ? "google_play_billing" : null;
  if (provider === null) return null;
  return capabilities.some(({ category, actionable, providers }) => category === "mobile_billing" && actionable && providers.includes(provider)) ? provider : null;
}

interface Trip {
  readonly tripId: string;
  readonly name: string;
  readonly destination: string;
  readonly startsOn: string;
  readonly endsOn: string;
}

interface PlannerResult {
  readonly outcome: "FEASIBLE" | "RISKY" | "NOT_FEASIBLE" | "INSUFFICIENT_DATA";
  readonly explanations: readonly string[];
  readonly missingInputs: readonly string[];
  readonly advisoryAcknowledgement: { readonly acknowledged: boolean; readonly statement: string };
}

interface AuthMethod {
  readonly provider: "apple" | "google" | "email_magic_link";
  readonly available: boolean;
  readonly passwordAllowed: false;
}

interface EventReminder {
  readonly reminderId: string;
  readonly event: {
    readonly eventId: string;
    readonly title: string;
    readonly startsAt: string;
    readonly endsAt: string;
  };
  readonly reminderKind: "24_HOURS" | "1_HOUR";
  readonly scheduledAt: string;
  readonly status: "SCHEDULED" | "DUE" | "IN_APP_DELIVERED" | "DISABLED" | "EXPIRED";
  readonly pushDelivery: {
    readonly status: "EXTERNAL_PROVIDER_NOT_CONFIGURED";
    readonly reason: string;
  };
}

const qualificationAccount = qualificationAccountFromLocation();
const AUTH_CALLBACK_URL = "https://portabilityapp.net/mobile/auth/callback";
const AUTH_RESULT_EVENT = "portability-auth-result";
const AUTH_RESULT_ACKNOWLEDGED_KEY = "portability.auth-result.acknowledged";
const processedAuthTransactions = new Set<string>();
let latestAuthResult: string | null = null;
const PRIMARY_NAVIGATION_FALLBACK: Bootstrap["navigation"] = Object.freeze([
  { id: "home", label: "Home" },
  { id: "explore", label: "Explore" },
  { id: "trips", label: "Trips" },
  { id: "live", label: "Live" },
  { id: "profile", label: "Profile" }
]);
const memoryStorageValues = new Map<string, string>();
const memoryStorage: PreferencesStorage = {
  getItem: (key) => memoryStorageValues.get(key) ?? null,
  setItem: (key, value) => { memoryStorageValues.set(key, value); }
};

function durableStorage(): PreferencesStorage {
  try {
    if (typeof globalThis.localStorage !== "undefined") return globalThis.localStorage;
  } catch {
    // Safari private mode and some embedded shells may deny storage access.
  }
  return memoryStorage;
}

function deviceLocales(): readonly string[] {
  if (typeof navigator === "undefined") return ["en-US"];
  return navigator.languages.length > 0 ? navigator.languages : [navigator.language || "en-US"];
}

function capabilityForHome(category: string, label: string, capabilities: Bootstrap["providerCapabilities"]): HomeExploreCapabilityInput {
  const match = capabilities.find((candidate) => candidate.category === category);
  return {
    id: category as HomeExploreCapabilityInput["id"],
    label,
    actionable: match?.actionable === true,
    providers: match?.providers ?? [],
    reason: match?.reason ?? "This option is not available right now."
  };
}

function authResultWasAcknowledged(): boolean {
  try {
    return globalThis.sessionStorage?.getItem(AUTH_RESULT_ACKNOWLEDGED_KEY) === "true";
  } catch {
    return false;
  }
}

function acknowledgeAuthResult() {
  latestAuthResult = null;
  try {
    globalThis.sessionStorage?.setItem(AUTH_RESULT_ACKNOWLEDGED_KEY, "true");
  } catch {
    // The acknowledgement still applies to this mounted app when storage is unavailable.
  }
}

function offlineStateFromSummary(summary: OfflineTripSummary | undefined): OfflineTripStatus["state"] {
  if (summary === undefined) return "not_saved";
  return summary.syncState;
}

function offlineSyncLabel(syncState: OfflineTripSummary["syncState"]): string {
  if (syncState === "pending") return "Waiting to sync";
  if (syncState === "conflict") return "Needs review";
  return "Up to date";
}

function membershipTierLabel(tier: Account["tier"]): string {
  return tier.replaceAll("_", " ").toLowerCase().replace(/\b\w/gu, (letter) => letter.toUpperCase());
}

function canonicalLocalDateTime(value: string | undefined): string | undefined {
  if (value === undefined || value.trim() === "") return undefined;
  const parsed = new Date(value);
  return Number.isFinite(parsed.getTime()) ? parsed.toISOString() : undefined;
}

function savedKindForOffer(offer: Pick<ProviderOffer, "category">): SavedTravelItem["kind"] {
  if (offer.category === "stays") return "hotel";
  if (offer.category === "tours" || offer.category === "activities") return "tour";
  if (offer.category === "events") return "event";
  if (offer.category === "offers") return "offer";
  if (offer.category === "local_guides") return "guide";
  return "place";
}

function savedKindForGlobalResult(result: Pick<ExploreGlobalResult, "detailKind" | "category">): SavedTravelItem["kind"] {
  if (result.detailKind === "hotel") return "hotel";
  if (result.detailKind === "tour" || result.detailKind === "activity") return "tour";
  if (result.detailKind === "offer") return "offer";
  if (result.detailKind === "local_guide") return "guide";
  if (result.detailKind === "livestream") return "livestream";
  if (result.detailKind === "event") return "event";
  if (result.category === "offers") return "offer";
  return "place";
}

function normalizedResultTitle(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/gu, " ");
}

function visibleGlobalResultsForOffers(results: readonly ExploreGlobalResult[], offers: readonly ProviderOffer[]): readonly ExploreGlobalResult[] {
  if (results.length === 0 || offers.length === 0) return results;
  const offerKeys = new Set(offers.map((offer) => `${savedKindForOffer(offer)}:${normalizedResultTitle(offer.title)}`));
  return results.filter((result) => !offerKeys.has(`${savedKindForGlobalResult(result)}:${normalizedResultTitle(result.title)}`));
}

function eventListingForReminder(offer: ExploreCard) {
  const startsAt = typeof offer.attributes.startsAt === "string" ? offer.attributes.startsAt : null;
  const endsAt = typeof offer.attributes.endsAt === "string" ? offer.attributes.endsAt : null;
  if (offer.category !== "events" || startsAt === null || endsAt === null) return null;
  if (!Number.isFinite(Date.parse(startsAt)) || !Number.isFinite(Date.parse(endsAt))) return null;
  return Object.freeze({
    eventId: offer.offerId,
    title: offer.title,
    venueKey: offer.locationLabel,
    startsAt: new Date(startsAt).toISOString(),
    endsAt: new Date(endsAt).toISOString(),
    source: offer.provenance.attribution,
    sourceReference: offer.providerPayloadReference,
    familyFriendly: offer.familyFriendly === true
  });
}

function readLocalAccountExploreState(storage: PreferencesStorage): AccountExploreState {
  return Object.freeze({ recentSearches: readRecentSearches(storage), savedItems: readSavedTravelItems(storage) });
}

function authCallbackUrl(): string {
  return Capacitor.isNativePlatform() ? AUTH_CALLBACK_URL : `${globalThis.location.origin}/mobile/auth/callback`;
}

function publishAuthResult(message: string) {
  latestAuthResult = message;
  try {
    globalThis.sessionStorage?.removeItem(AUTH_RESULT_ACKNOWLEDGED_KEY);
  } catch {
    // A fresh result is still delivered to the mounted app.
  }
  globalThis.dispatchEvent(new CustomEvent(AUTH_RESULT_EVENT, { detail: message }));
}

async function completeAuthenticationFromUrl(value: string): Promise<void> {
  let callback: URL;
  try {
    callback = new URL(value);
  } catch {
    return;
  }
  const trustedOrigin = callback.origin === "https://portabilityapp.net" || callback.origin === "https://www.portabilityapp.net";
  if (!trustedOrigin || callback.pathname !== "/mobile/auth/callback") return;
  const transactionId = callback.searchParams.get("portability_auth_tx");
  const code = callback.searchParams.get("code");
  const provider = callback.searchParams.get("provider");
  const callbackErrorMessage = authenticationCallbackErrorMessage(callback.searchParams);
  if (globalThis.location.href === callback.href) globalThis.history.replaceState({}, "", "/mobile/");
  if (callbackErrorMessage !== null) {
    publishAuthResult(callbackErrorMessage);
    return;
  }
  if (!transactionId || !code || (provider !== "apple" && provider !== "google" && provider !== "email_magic_link") || processedAuthTransactions.has(transactionId)) return;
  processedAuthTransactions.add(transactionId);
  try {
    await apiRequest("/v1/auth/complete", { method: "POST", body: { provider, transactionId, code } });
    publishAuthResult("Sign-in completed securely.");
  } catch (error) {
    processedAuthTransactions.delete(transactionId);
    publishAuthResult(error instanceof Error ? error.message : "Sign-in could not be completed");
  }
}

void completeAuthenticationFromUrl(globalThis.location.href);
void CapacitorApp.addListener("appUrlOpen", ({ url }) => { void completeAuthenticationFromUrl(url); });

function Field(props: { readonly label: string; readonly name: string; readonly type?: string; readonly autoComplete?: string; readonly required?: boolean; readonly defaultValue?: string; readonly disabled?: boolean; readonly children?: React.ReactNode }) {
  return <label className="field">{props.label}{props.children ?? <input name={props.name} type={props.type ?? "text"} autoComplete={props.autoComplete} required={props.required} defaultValue={props.defaultValue} disabled={props.disabled} />}</label>;
}

function categoryLabel(category: string): string {
  return category.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function Home(props: { readonly bootstrap: Bootstrap; readonly liveServicesAvailable: boolean; readonly locale: RequiredLocaleCode; readonly onNavigate: (tab: Tab) => void }) {
  const copy = (key: PortabilityMessageKey) => localizedMessage(props.locale, key);
  const [planner, setPlanner] = useState<PlannerResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [helperTrips, setHelperTrips] = useState<readonly HomeExploreTrip[]>([]);
  const [savedCounts, setSavedCounts] = useState<HomeExploreSavedCounts>({ places: 0, offers: 0, events: 0, livestreams: 0, offlineTrips: 0, offlineTickets: 0, offlineNotes: 0 });
  const [pointsState, setPointsState] = useState<HomeExplorePointsState>({ balance: null, source: props.bootstrap.account === null ? "signed_out" : "unavailable", reason: props.bootstrap.account === null ? "Sign in to see your points." : "Loading your points." });
  const [agenticOpen, setAgenticOpen] = useState(false);
  const [agenticAnswer, setAgenticAnswer] = useState<string | null>(null);
  const [microphonePermission, setMicrophonePermission] = useState<"PROMPT" | "GRANTED" | "DENIED">("PROMPT");
  const [voiceCapture, setVoiceCapture] = useState<AgenticVoiceCaptureState>({ requestId: null, status: "IDLE", transcript: null, error: null, typedFallbackAvailable: true, rawAudioRetained: false });
  const [voiceOutput, setVoiceOutput] = useState<AgenticVoiceOutputState>({ requestId: null, status: "IDLE", encryptedAudioReference: null, expiresAt: null, error: null, typedFallbackAvailable: true });
  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const mediaChunks = useRef<Blob[]>([]);

  const capability = (category: string) => props.bootstrap.providerCapabilities.find((candidate) => candidate.category === category && candidate.actionable && candidate.providers.length > 0);
  const agenticCapability = capability("agentic");
  const speechCapability = capability("speech_to_text");
  const voiceCapability = capability("text_to_speech");
  const translationCapability = capability("translation");

  useEffect(() => {
    let disposed = false;
    async function loadHomeContext() {
      const storage = durableStorage();
      const savedItems = readSavedTravelItems(storage);
      const offline = await listEncryptedOfflineTrips().catch(() => []);
      const serverTrips = props.bootstrap.account === null
        ? []
        : await apiRequest<{ readonly trips: readonly Trip[] }>("/v1/trips", { qualificationAccount }).then(({ trips }) => trips).catch(() => []);
      const commerce = props.bootstrap.account === null
        ? null
        : await apiRequest<{ readonly points?: { readonly balance?: number } }>("/v1/commerce/account/summary", { qualificationAccount }).catch(() => null);
      if (disposed) return;
      const offlineIds = new Set(offline.map(({ tripId }) => tripId));
      setSavedCounts({
        places: savedItems.filter(({ kind }) => kind === "place" || kind === "hotel" || kind === "tour" || kind === "guide").length,
        offers: savedItems.filter(({ kind }) => kind === "offer").length,
        events: savedItems.filter(({ kind }) => kind === "event").length,
        livestreams: savedItems.filter(({ kind }) => kind === "livestream").length,
        offlineTrips: offline.length,
        offlineTickets: 0,
        offlineNotes: offline.reduce((total, trip) => total + (trip.syncState === "pending" ? 1 : 0), 0)
      });
      setPointsState(commerce === null
        ? { balance: null, source: props.bootstrap.account === null ? "signed_out" : "unavailable", reason: props.bootstrap.account === null ? "Sign in to see your points." : "Points are temporarily unavailable." }
        : { balance: typeof commerce.points?.balance === "number" ? commerce.points.balance : null, source: "server", reason: "Loaded from your Portability account." });
      setHelperTrips(serverTrips.map((trip) => ({
        tripId: trip.tripId,
        name: trip.name,
        destination: trip.destination,
        startsAt: `${trip.startsOn}T00:00:00.000Z`,
        endsAt: `${trip.endsOn}T23:59:59.000Z`,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        savedOffline: offlineIds.has(trip.tripId),
        offlineItemCount: 0
      })));
    }
    void loadHomeContext();
    return () => { disposed = true; };
  }, [props.bootstrap.account?.accountId]);

  async function submitPlanner(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!props.liveServicesAvailable) {
      setError("Timing checks are temporarily unavailable.");
      return;
    }
    setError(null);
    const data = new FormData(event.currentTarget);
    const availableFrom = String(data.get("availableFrom"));
    const deadlineAt = String(data.get("deadlineAt"));
    const requestedAt = new Date().toISOString();
    try {
      const result = await apiRequest<PlannerResult>("/v1/planner", {
        method: "POST",
        body: {
          input: {
            requestedAt,
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            availableFrom: new Date(availableFrom).toISOString(),
            deadlineAt: new Date(deadlineAt).toISOString(),
            sourceRecords: [{ name: "traveler-entered-window", capturedAt: requestedAt, maxAgeMinutes: 5, required: true }],
            transfers: { outbound: 20, return: 20 },
            buffers: { safety: 30 },
            activities: [{ id: "activity", label: String(data.get("activity")), durationMinutes: Number(data.get("duration")) }],
            assumptions: ["Travel time entered as a conservative local estimate."],
            requireOpeningHours: false
          },
          acknowledgement: { accountId: props.bootstrap.account?.accountId ?? "signed-out-traveler", acknowledgedAt: requestedAt }
        }
      });
      setPlanner(result);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Planner request failed");
    }
  }

  async function askAgentic(input: { readonly prompt: string; readonly tripId: string | null; readonly targetLocale: string | null }) {
    if (props.bootstrap.account === null || agenticCapability === undefined) {
      setAgenticAnswer("Sign in to ask a live travel question when assistance is available.");
      return;
    }
    try {
      const result = await apiRequest<AgenticTextResult>("/v1/agentic/text", {
        method: "POST",
        qualificationAccount,
        body: {
          requestId: crypto.randomUUID(),
          prompt: input.prompt,
          locale: input.targetLocale || navigator.language || "en-US",
          allowedTripIds: input.tripId === null ? [] : [input.tripId]
        }
      });
      setAgenticAnswer(result.text);
    } catch (caught) {
      setAgenticAnswer(caught instanceof Error ? caught.message : "Agentic assistance is unavailable.");
    }
  }

  async function requestMicrophone() {
    if (speechCapability === undefined) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setMicrophonePermission("GRANTED");
      const requestId = crypto.randomUUID();
      mediaChunks.current = [];
      const recorder = new MediaRecorder(stream);
      mediaRecorder.current = recorder;
      recorder.addEventListener("dataavailable", (event) => { if (event.data.size > 0) mediaChunks.current.push(event.data); });
      recorder.addEventListener("stop", () => {
        stream.getTracks().forEach((track) => track.stop());
        void submitCapturedSpeech(requestId, new Blob(mediaChunks.current, { type: recorder.mimeType || "audio/webm" }));
      }, { once: true });
      recorder.start();
      setVoiceCapture({ requestId, status: "CAPTURING", transcript: null, error: null, typedFallbackAvailable: true, rawAudioRetained: false });
      globalThis.setTimeout(() => { if (recorder.state === "recording") recorder.stop(); }, 30_000);
    } catch {
      setMicrophonePermission("DENIED");
      setVoiceCapture({ requestId: null, status: "ERROR", transcript: null, error: "Microphone access was not granted. Typed assistance remains available.", typedFallbackAvailable: true, rawAudioRetained: false });
    }
  }

  async function submitCapturedSpeech(requestId: string, audio: Blob) {
    setVoiceCapture({ requestId, status: "UPLOADING", transcript: null, error: null, typedFallbackAvailable: true, rawAudioRetained: false });
    try {
      if (audio.size === 0 || audio.size > 650_000) throw new Error(audio.size === 0 ? "No speech audio was captured." : "Voice recording exceeded the 30-second secure upload limit.");
      const encryptedAudioReference = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.addEventListener("load", () => typeof reader.result === "string" ? resolve(reader.result) : reject(new Error("Voice audio could not be prepared.")), { once: true });
        reader.addEventListener("error", () => reject(new Error("Voice audio could not be prepared.")), { once: true });
        reader.readAsDataURL(audio);
      });
      setVoiceCapture({ requestId, status: "TRANSCRIBING", transcript: null, error: null, typedFallbackAvailable: true, rawAudioRetained: false });
      const result = await apiRequest<{ readonly status: string; readonly transcript: string | null }>("/v1/agentic/speech/transcribe", {
        method: "POST",
        qualificationAccount,
        body: { requestId, encryptedAudioReference, locale: navigator.language || "en-US", userPressedMicrophone: true, permission: "GRANTED" }
      });
      setVoiceCapture({ requestId, status: result.transcript === null ? "ERROR" : "TRANSCRIBED", transcript: result.transcript, error: result.transcript === null ? "Speech could not be transcribed. Typed assistance remains available." : null, typedFallbackAvailable: true, rawAudioRetained: false });
    } catch (caught) {
      setVoiceCapture({ requestId, status: "ERROR", transcript: null, error: caught instanceof Error ? caught.message : "Speech could not be transcribed.", typedFallbackAvailable: true, rawAudioRetained: false });
    } finally {
      mediaChunks.current = [];
      mediaRecorder.current = null;
    }
  }

  function stopMicrophone() {
    if (mediaRecorder.current?.state === "recording") mediaRecorder.current.stop();
  }

  async function requestVoiceOutput(input: { readonly text: string; readonly disclosureAcknowledged: boolean }) {
    if (voiceCapability === undefined) return;
    const requestId = crypto.randomUUID();
    setVoiceOutput({ requestId, status: "QUEUED", encryptedAudioReference: null, expiresAt: null, error: null, typedFallbackAvailable: true });
    try {
      const result = await apiRequest<{ readonly status: string; readonly encryptedAudioReference: string | null; readonly expiresAt: string | null }>("/v1/agentic/tts", {
        method: "POST",
        qualificationAccount,
        body: { requestId, text: input.text, locale: navigator.language || "en-US", userOptedIn: true, disclosureAcknowledged: input.disclosureAcknowledged }
      });
      setVoiceOutput({ requestId, status: result.encryptedAudioReference === null ? "ERROR" : "READY", encryptedAudioReference: result.encryptedAudioReference, expiresAt: result.expiresAt, error: result.encryptedAudioReference === null ? "Voice playback is unavailable. The typed answer remains available." : null, typedFallbackAvailable: true });
    } catch (caught) {
      setVoiceOutput({ requestId, status: "ERROR", encryptedAudioReference: null, expiresAt: null, error: caught instanceof Error ? caught.message : "Voice output is unavailable.", typedFallbackAvailable: true });
    }
  }

  async function playVoiceOutput(input: { readonly requestId: string; readonly encryptedAudioReference: string }) {
    try {
      const audio = new Audio(input.encryptedAudioReference);
      setVoiceOutput((current) => ({ ...current, status: "PLAYING", error: null }));
      await audio.play();
      audio.addEventListener("ended", () => setVoiceOutput((current) => ({ ...current, status: "READY" })), { once: true });
    } catch {
      setVoiceOutput((current) => ({ ...current, status: "ERROR", error: "Secure voice playback could not start. The typed answer remains available." }));
    }
  }

  async function cancelVoiceOutput(input: { readonly requestId: string }) {
    try {
      await apiRequest(`/v1/agentic/tts/${encodeURIComponent(input.requestId)}`, { method: "DELETE", qualificationAccount });
    } finally {
      setVoiceOutput((current) => ({ ...current, status: "CANCELLED", encryptedAudioReference: null }));
    }
  }

  async function escalateAgenticSupport(input: { readonly summary: string; readonly replyEmail: string }) {
    try {
      await apiRequest("/v1/support-cases", { method: "POST", qualificationAccount, body: { category: "OTHER_UNRESOLVED", summary: input.summary, replyEmail: input.replyEmail } });
      setAgenticAnswer("Your support case was created. Human follow-up will use the email you provided.");
    } catch (caught) {
      setAgenticAnswer(caught instanceof Error ? caught.message : "The support case could not be created.");
    }
  }

  return <>
    <section className="hero" aria-labelledby="home-title">
      <span className="badge">{copy("mobile.heroBadge")}</span>
      <h2 id="home-title">{copy("mobile.heroTitle")}</h2>
      <p>{copy("mobile.heroBody")}</p>
      <div className="button-row">
        <button className="primary" type="button" disabled={!props.liveServicesAvailable} onClick={() => props.onNavigate("trips")}>{copy("action.createTrip")}</button>
        <button className="secondary" type="button" disabled={!props.liveServicesAvailable} onClick={() => document.querySelector("#planner")?.scrollIntoView()}>{copy("action.checkTiming")}</button>
      </div>
      {!props.liveServicesAvailable && <p className="limited-mode">{copy("mobile.limitedMode")}</p>}
    </section>
    <section className="section grid" aria-label={copy("mobile.tripOverview")}>
      <article className="card"><span className="eyebrow">TRIPS</span><div className="metric">{helperTrips.length}</div><p>{props.bootstrap.account === null ? "Sign in to sync cloud trips; device offline essentials stay local." : "Loaded from your Portability account."}</p></article>
      <article className="card"><span className="eyebrow">{copy("mobile.points").toUpperCase()}</span><div className="metric">{typeof pointsState.balance === "number" ? pointsState.balance.toLocaleString() : "-"}</div><p>{pointsState.reason}</p></article>
      <article className="card"><span className="eyebrow">LIVE</span><h2>{props.liveServicesAvailable ? copy("mobile.connected") : copy("mobile.limited")}</h2><p>{props.liveServicesAvailable ? "Live actions open only when the right travel source is ready." : "Browse, saved items, and offline records remain visible while live actions stay gated."}</p></article>
    </section>
    <section className="section card" aria-labelledby="agentic-entry-title">
      <div className="section-heading"><div><span className="eyebrow">{copy("mobile.askPortability").toUpperCase()}</span><h2 id="agentic-entry-title">{copy("mobile.companionTitle")}</h2></div></div>
      <p>{copy("mobile.companionBody")}</p>
      <button className="primary" type="button" onClick={() => setAgenticOpen((open) => !open)}>{agenticOpen ? copy("mobile.closeCompanion") : copy("mobile.openCompanion")}</button>
    </section>
    <AgenticTravelCompanionPanel
      openedByUser={agenticOpen}
      accountTier={props.bootstrap.account?.tier ?? "FREE"}
      trips={helperTrips.map(({ tripId, name, destination }) => ({ tripId, name, destination }))}
      capabilities={{
        text: props.bootstrap.account !== null && agenticCapability !== undefined,
        speechToText: speechCapability !== undefined,
        textToSpeech: voiceCapability !== undefined,
        translation: translationCapability !== undefined,
        supportEscalation: props.bootstrap.account !== null && props.liveServicesAvailable,
        unavailableReason: agenticCapability === undefined ? "Travel help is temporarily unavailable." : null
      }}
      microphonePermission={microphonePermission}
      lastAnswer={agenticAnswer}
      voiceCapture={voiceCapture}
      voiceOutput={voiceOutput}
      callbacks={{
        onAskTyped: askAgentic,
        onStartPushToTalk: requestMicrophone,
        onStopPushToTalk: stopMicrophone,
        onSubmitVoiceTranscript: ({ prompt, tripId, targetLocale }) => askAgentic({ prompt, tripId, targetLocale }),
        onRequestVoiceOutput: requestVoiceOutput,
        onPlayVoiceOutput: playVoiceOutput,
        onCancelVoiceOutput: cancelVoiceOutput,
        onEscalateSupport: escalateAgenticSupport
      }}
    />
    <section id="planner" className="section card" aria-labelledby="planner-title">
      <div className="section-heading"><div><span className="eyebrow">{copy("mobile.plannerEyebrow").toUpperCase()}</span><h2 id="planner-title">{copy("mobile.plannerTitle")}</h2></div><p>{copy("mobile.plannerAuthority")}</p></div>
      <form className="form" onSubmit={submitPlanner}>
        <div className="grid">
          <Field label="Available from" name="availableFrom" type="datetime-local" required />
          <Field label="Must be back by" name="deadlineAt" type="datetime-local" required />
          <Field label="Activity" name="activity" required defaultValue="Explore nearby" />
          <Field label="Activity minutes" name="duration" type="number" required defaultValue="60" />
        </div>
        <label><input name="acknowledged" type="checkbox" required /> I understand these estimates are advisory, conditions can change, and I remain responsible for all deadlines.</label>
        <button className="primary" type="submit" disabled={!props.liveServicesAvailable}>Evaluate timing</button>
      </form>
      {!props.liveServicesAvailable && <p className="muted">Timing inputs remain visible as a preview, but no feasibility result is produced until timing checks are ready.</p>}
      {error && <p className="error" role="alert">{error}</p>}
      {planner && <div className="notice" aria-live="polite"><strong>{planner.outcome.replaceAll("_", " ")}</strong><ul>{planner.explanations.map((explanation) => <li key={explanation}>{explanation}</li>)}</ul>{planner.missingInputs.length > 0 && <p>Needed: {planner.missingInputs.join(", ")}</p>}</div>}
    </section>
    <HomeExploreHelperPanel
      trips={helperTrips}
      savedCounts={savedCounts}
      points={pointsState}
      now={new Date().toISOString()}
      signedIn={props.bootstrap.account !== null}
      liveServicesAvailable={props.liveServicesAvailable}
      capabilities={[
        { id: "explore_search", label: "Travel search", actionable: props.bootstrap.providerCapabilities.some(({ actionable, providers }) => actionable && providers.length > 0), providers: props.bootstrap.providerCapabilities.flatMap(({ providers }) => providers), reason: "Search is temporarily unavailable." },
        { id: "deals", label: "Deals", actionable: props.bootstrap.providerCapabilities.some(({ category, actionable, providers }) => category === "offers" && actionable && providers.length > 0), providers: props.bootstrap.providerCapabilities.find(({ category }) => category === "offers")?.providers ?? [], reason: "Offer providers are not active yet." },
        { id: "trip_sync", label: "Trips", actionable: props.liveServicesAvailable, providers: props.liveServicesAvailable ? ["portability-runtime"] : [], reason: "Trip sync is temporarily unavailable." },
        { id: "offline_essentials", label: "Offline essentials", actionable: true, providers: ["device-cache"], reason: "Offline essentials are available on this device." },
        { id: "planner", label: "Planner", actionable: props.liveServicesAvailable, providers: props.liveServicesAvailable ? ["deterministic-planner"] : [], reason: "Timing checks are temporarily unavailable." }
      ]}
      onAction={(action) => {
        if (action === "open_trips" || action === "manage_offline") props.onNavigate("trips");
        if (action === "search_explore" || action === "open_saved") props.onNavigate("explore");
        if (action === "sign_in") props.onNavigate("profile");
        if (action === "plan_timing") document.querySelector("#planner")?.scrollIntoView();
      }}
    />
  </>;
}

function Explore(props: { readonly bootstrap: Bootstrap; readonly locale: RequiredLocaleCode; readonly preferences: MobilePreferences; readonly onExploreStateChange: (state: AccountExploreState) => void }) {
  const copy = (key: PortabilityMessageKey) => localizedMessage(props.locale, key);
  const storage = durableStorage();
  const [query, setQuery] = useState("");
  const searchableCategories = useMemo(() => deriveExploreSearchCategories(props.bootstrap.providerCapabilities), [props.bootstrap.providerCapabilities]);
  const [category, setCategory] = useState<DiscoveryCategory>(() => resolveExploreSearchCategory(props.bootstrap.providerCapabilities, "stays"));
  const [view, setView] = useState<"list" | "map">("list");
  const [sort, setSort] = useState<ExploreSort>("BEST_OVERALL_VALUE");
  const [familyFriendly, setFamilyFriendly] = useState(false);
  const [openNow, setOpenNow] = useState(false);
  const [offers, setOffers] = useState<readonly ProviderOffer[]>([]);
  const [globalResults, setGlobalResults] = useState<readonly ExploreGlobalResult[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [recent, setRecent] = useState(() => readRecentSearches(storage));
  const [saved, setSaved] = useState(() => readSavedTravelItems(storage));
  const [eventReminders, setEventReminders] = useState<readonly EventReminder[]>([]);
  const [selectedOffer, setSelectedOffer] = useState<ProviderOffer | null>(null);
  const availability = resolveExploreSearchAvailability(props.bootstrap.providerCapabilities, category);
  const dealsAvailable = props.bootstrap.providerCapabilities.some(({ category: capabilityCategory, actionable, providers }) => capabilityCategory === "offers" && actionable && providers.length > 0);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string>(availability.message);
  const savedIds = useMemo(() => new Set(saved.map((item) => item.targetId)), [saved]);
  const eventReminderIds = useMemo(() => new Set(eventReminders.filter(({ status }) => status !== "DISABLED" && status !== "EXPIRED").map(({ event }) => event.eventId)), [eventReminders]);
  const visibleOffers = useMemo(() => {
    const filtered = offers
      .filter((offer) => !familyFriendly || offer.familyFriendly === true)
      .filter((offer) => !openNow || offer.attributes.openNow === true);
    return sortExploreOffers(filtered, sort);
  }, [familyFriendly, offers, openNow, sort]);
  const visibleGlobalResults = useMemo(() => visibleGlobalResultsForOffers(globalResults, offers), [globalResults, offers]);

  useEffect(() => {
    const nextCategory = resolveExploreSearchCategory(props.bootstrap.providerCapabilities, category);
    if (nextCategory !== category) {
      setCategory(nextCategory);
      return;
    }
    setOffers([]);
    setGlobalResults([]);
    setSelectedOffer(null);
    setHasSearched(false);
    setMessage(resolveExploreSearchAvailability(props.bootstrap.providerCapabilities, category).message);
  }, [category, props.bootstrap.providerCapabilities]);

  async function loadEventReminders() {
    if (props.bootstrap.account === null) {
      setEventReminders([]);
      return;
    }
    try {
      const response = await apiRequest<{ readonly reminders: readonly EventReminder[] }>("/v1/account/event-reminders", { qualificationAccount });
      setEventReminders(Array.isArray(response.reminders) ? response.reminders : []);
    } catch {
      setEventReminders([]);
    }
  }

  useEffect(() => { void loadEventReminders(); }, [props.bootstrap.account?.accountId]);

  async function search(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmedQuery = query.trim();
    if (!trimmedQuery) {
      setMessage("Enter a place, airport, port, event, or category before searching.");
      return;
    }
    setHasSearched(true);
    if (searchableCategories.length === 0) {
      setOffers([]);
      setGlobalResults([]);
      setMessage("Live search is temporarily unavailable.");
      return;
    }
    if (!availability.available) {
      setOffers([]);
      setGlobalResults([]);
      setBusy(true);
      try {
        const params = new URLSearchParams({ q: trimmedQuery, category, familyFriendly: familyFriendly ? "true" : "false", sort });
        const result = await apiRequest<{ readonly globalResults: readonly ExploreGlobalResult[]; readonly providerExecutionConfigured: boolean; readonly fabricatedInventory: false }>(`/v1/explore?${params.toString()}`, { qualificationAccount });
        setGlobalResults(result.globalResults);
        const nextRecent = recordRecentSearch(storage, { query: trimmedQuery, category, searchedAt: new Date().toISOString() });
        setRecent(nextRecent);
        props.onExploreStateChange(Object.freeze({ recentSearches: nextRecent, savedItems: readSavedTravelItems(storage) }));
        setMessage(result.globalResults.length === 0 ? availability.message : `${result.globalResults.length} result${result.globalResults.length === 1 ? "" : "s"} found.`);
      } catch (caught) {
        setMessage(caught instanceof Error ? caught.message : availability.message);
      } finally {
        setBusy(false);
      }
      return;
    }
    setBusy(true);
    setMessage("Searching...");
    try {
      const params = new URLSearchParams({ q: trimmedQuery, category, familyFriendly: familyFriendly ? "true" : "false", sort });
      const result = await apiRequest<{ readonly offers: readonly ProviderOffer[]; readonly globalResults: readonly ExploreGlobalResult[]; readonly unavailableProviders: readonly { readonly provider: string; readonly reason: string }[]; readonly providerExecutionConfigured: boolean; readonly fabricatedInventory: false }>(`/v1/explore?${params.toString()}`, { qualificationAccount });
      setOffers(result.offers);
      const nextGlobalResults = visibleGlobalResultsForOffers(result.globalResults, result.offers);
      setGlobalResults(nextGlobalResults);
      const nextRecent = recordRecentSearch(storage, { query: trimmedQuery, category, searchedAt: new Date().toISOString() });
      setRecent(nextRecent);
      props.onExploreStateChange(Object.freeze({ recentSearches: nextRecent, savedItems: readSavedTravelItems(storage) }));
      if (!result.providerExecutionConfigured) setMessage("This category is not available yet because an approved source is not active. No results were substituted.");
      else if (result.offers.length === 0 && nextGlobalResults.length === 0) setMessage(result.unavailableProviders.length > 0 ? "This category is not available yet because an approved source is not active. No results were substituted." : "No verified results matched this request.");
      else setMessage(`${result.offers.length + nextGlobalResults.length} result${result.offers.length + nextGlobalResults.length === 1 ? "" : "s"} found.`);
    } catch (caught) {
      setOffers([]);
      setMessage(caught instanceof Error ? caught.message : "Live search is temporarily unavailable.");
    } finally {
      setBusy(false);
    }
  }

  function saveOffer(offer: ExploreCard) {
    const nextSaved = toggleSavedTravelItem(storage, {
      kind: savedKindForOffer(offer as ProviderOffer),
      targetId: offer.offerId,
      title: offer.title,
      sourceReference: offer.provenance.attribution,
      savedAt: new Date().toISOString()
    });
    setSaved(nextSaved);
    props.onExploreStateChange(Object.freeze({ recentSearches: readRecentSearches(storage), savedItems: nextSaved }));
  }

  function saveGlobalResult(result: ExploreGlobalResult) {
    const kind = savedKindForGlobalResult(result);
    const nextSaved = toggleSavedTravelItem(storage, {
      kind,
      targetId: result.id,
      title: result.title,
      sourceReference: result.sourceReference,
      savedAt: new Date().toISOString()
    });
    setSaved(nextSaved);
    setGlobalResults((existing) => existing.map((candidate) => candidate.id === result.id && candidate.detailKind === result.detailKind ? { ...candidate, saved: nextSaved.some((item) => item.kind === kind && item.targetId === result.id) } : candidate));
    props.onExploreStateChange(Object.freeze({ recentSearches: readRecentSearches(storage), savedItems: nextSaved }));
  }

  function openGlobalResult(result: ExploreGlobalResult) {
    setMessage(`${result.title}${result.actions.continueWithProvider ? " can be opened from its details." : " can be viewed only in Portability. No external handoff is available."}`);
  }

  async function saveEventReminder(offer: ExploreCard) {
    const event = eventListingForReminder(offer);
    if (event === null) {
      setMessage("This event does not include provider-supplied start and end times, so Portability cannot schedule reminders.");
      return;
    }
    if (props.bootstrap.account === null) {
      setMessage("Sign in to sync event reminders across devices. The event can still stay saved on this device.");
      saveOffer(offer);
      return;
    }
    try {
      const response = await apiRequest<{ readonly saved: readonly EventReminder[]; readonly externalPushDelivery: { readonly reason: string } }>("/v1/account/event-reminders", {
        method: "POST",
        qualificationAccount,
        body: {
          event,
          preferences: {
            twentyFourHours: props.preferences.notifications.event24Hours,
            oneHour: props.preferences.notifications.event1Hour
          }
        }
      });
      if (!savedIds.has(offer.offerId)) saveOffer(offer);
      await loadEventReminders();
      setMessage(`${response.saved.length} event reminder${response.saved.length === 1 ? "" : "s"} scheduled. Push alerts depend on your device notification settings.`);
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "Event reminders could not be scheduled.");
    }
  }

  return <section aria-labelledby="explore-title">
    <div className="section-heading"><div><span className="eyebrow">{copy("nav.explore").toUpperCase()}</span><h2 id="explore-title">{copy("mobile.exploreTitle")}</h2></div><p>{copy("mobile.exploreBody")}</p></div>
    <form className="card form" onSubmit={search}>
      <Field label="Destination, airport, port, event, guide, or place" name="query"><input name="query" value={query} onChange={(event) => setQuery(event.currentTarget.value)} required /></Field>
      <Field label="Category" name="category"><select name="category" value={searchableCategories.length === 0 ? "" : category} disabled={searchableCategories.length === 0} onChange={(event) => setCategory(event.currentTarget.value as DiscoveryCategory)}>{searchableCategories.length === 0 ? <option value="">Search unavailable</option> : searchableCategories.map((value) => <option value={value} key={value}>{categoryLabel(value)}</option>)}</select></Field>
      <ExploreFilters locale={props.preferences.locale} familyFriendly={familyFriendly} openNow={openNow} sort={sort} onFamilyFriendly={setFamilyFriendly} onOpenNow={setOpenNow} onSort={setSort} />
      <button className="primary" type="submit" disabled={busy}>{busy ? `${copy("action.search")}...` : copy("action.search")}</button>
      <p className="muted" aria-live="polite">{message}</p>
    </form>
    {recent.length > 0 && <section className="section card" aria-label="Recent searches"><span className="eyebrow">RECENT</span><div className="button-row">{recent.map((item) => <button className="secondary compact" type="button" key={`${item.category}:${item.query}`} onClick={() => { setQuery(item.query); setCategory(item.category as DiscoveryCategory); }}>{item.query}</button>)}</div></section>}
    <div className="button-row" role="group" aria-label="Explore view"><button className={view === "list" ? "primary" : "secondary"} type="button" onClick={() => setView("list")}>List</button><button className={view === "map" ? "primary" : "secondary"} type="button" onClick={() => setView("map")}>Map</button></div>
    {eventReminders.length > 0 && <section className="section card" aria-labelledby="event-reminders-title"><span className="eyebrow">REMINDERS</span><h3 id="event-reminders-title">Saved event reminders</h3><ul className="plain-list">{eventReminders.slice(0, 6).map((reminder) => <li key={reminder.reminderId}><strong>{reminder.event.title}</strong><p>{reminder.reminderKind.replace("_", " ").toLowerCase()} · {new Date(reminder.scheduledAt).toLocaleString()} · {reminder.status.replaceAll("_", " ").toLowerCase()}</p></li>)}</ul></section>}
    <ExploreGlobalResults locale={props.preferences.locale} results={visibleGlobalResults} savedIds={savedIds} onOpen={openGlobalResult} onSave={saveGlobalResult} />
    {hasSearched && <ExploreResults locale={props.preferences.locale} offers={visibleOffers} view={view} savedIds={savedIds} reminderIds={eventReminderIds} onOpen={(offer) => setSelectedOffer(offer as ProviderOffer)} onSave={saveOffer} onSaveEventReminder={(offer) => void saveEventReminder(offer)} />}
    {selectedOffer && <ExploreDetail locale={props.preferences.locale} offer={selectedOffer} reminderSaved={eventReminderIds.has(selectedOffer.offerId)} onClose={() => setSelectedOffer(null)} onSave={saveOffer} onSaveEventReminder={(offer) => void saveEventReminder(offer)} />}
    {dealsAvailable && <article className="section card"><h3>{copy("mobile.deals")}</h3><p>Verified offers for your search will appear with the results above.</p></article>}
  </section>;
}

function Trips(props: { readonly bootstrap: Bootstrap; readonly liveServicesAvailable: boolean; readonly locale: RequiredLocaleCode; readonly onNavigate: (tab: Tab) => void; readonly refresh: () => Promise<void> }) {
  const copy = (key: PortabilityMessageKey) => localizedMessage(props.locale, key);
  const text = (english: string, translations: Partial<Record<Exclude<RequiredLocaleCode, "en">, string>>) => mobileInlineCopy(props.locale, english, translations);
  const [trips, setTrips] = useState<readonly Trip[]>([]);
  const [offlineTrips, setOfflineTrips] = useState<readonly OfflineTripSummary[]>([]);
  const [offlineDetail, setOfflineDetail] = useState<OfflineTripEssentialsPayload | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [selectedTripId, setSelectedTripId] = useState<string | null>(null);
  const [itemsByTripId, setItemsByTripId] = useState<Readonly<Record<string, readonly TripItineraryItem[]>>>({});
  const [shareStates, setShareStates] = useState<Readonly<Record<string, TripShareState | undefined>>>({});
  const [plannerResult, setPlannerResult] = useState<PlannerResultSummary | null>(null);
  const [agenticResult, setAgenticResult] = useState<AgenticTextResult | null>(null);
  const [busyActions, setBusyActions] = useState<readonly string[]>([]);
  const [online, setOnline] = useState(() => typeof navigator === "undefined" ? true : navigator.onLine);

  async function loadTrips() {
    await refreshOfflineTrips();
    if (props.bootstrap.account === null) return;
    try {
      const result = await apiRequest<{ readonly trips: readonly Trip[] }>("/v1/trips", { qualificationAccount });
      setTrips(result.trips);
      const firstTrip = result.trips[0];
      if (firstTrip !== undefined && (selectedTripId === null || !result.trips.some((trip) => trip.tripId === selectedTripId))) setSelectedTripId(firstTrip.tripId);
      const details = await Promise.all(result.trips.map(async (trip) => {
        try {
          const detail = await apiRequest<{ readonly trip: Trip; readonly items: readonly TripItineraryItem[] }>(`/v1/trips/${encodeURIComponent(trip.tripId)}`, { qualificationAccount });
          return [trip.tripId, detail.items] as const;
        } catch {
          return [trip.tripId, []] as const;
        }
      }));
      setItemsByTripId(Object.fromEntries(details));
    } catch (caught) {
      setMessage(offlineTrips.length > 0 ? "Cloud trips are unavailable. Showing offline essentials saved on this device." : caught instanceof Error ? caught.message : "Trips could not be loaded");
    }
  }

  useEffect(() => { void loadTrips(); }, [props.bootstrap.account?.accountId]);
  useEffect(() => {
    function updateOnlineStatus() {
      const isOnline = navigator.onLine;
      setOnline(isOnline);
      if (isOnline) void syncOfflineTrips();
    }
    addEventListener("online", updateOnlineStatus);
    addEventListener("offline", updateOnlineStatus);
    return () => {
      removeEventListener("online", updateOnlineStatus);
      removeEventListener("offline", updateOnlineStatus);
    };
  }, []);

  async function refreshOfflineTrips() {
    try {
      setOfflineTrips(await listEncryptedOfflineTrips());
    } catch {
      setOfflineTrips([]);
    }
  }

  async function viewOfflineTrip(tripId: string) {
    const detail = await loadEncryptedOfflineTrip<OfflineTripEssentialsPayload>(tripId);
    setOfflineDetail(detail);
      setMessage(detail === null ? "Offline essentials are no longer present on this device." : `${detail.trip.name} is open from encrypted device storage. Changes, payments, and live availability require internet.`);
  }

  async function removeOfflineTrip(tripId: string) {
    await deleteEncryptedOfflineTrip(tripId);
    if (offlineDetail?.trip.tripId === tripId) setOfflineDetail(null);
    await refreshOfflineTrips();
    setMessage("Offline essentials deleted from this device.");
  }

  async function syncOfflineTrips() {
    const results = await syncEncryptedOfflineTrips(async ({ tripId, mutations }) => {
      if (!online) return { tripId, status: "skipped" };
      const response = await apiRequest<{ readonly status: "synced"; readonly payload: OfflineTripEssentialsPayload }>(`/v1/trips/${encodeURIComponent(tripId)}/offline/sync`, {
        method: "POST",
        qualificationAccount,
        body: { mutations }
      });
      return { tripId, status: response.status, serverPayload: response.payload };
    });
    await refreshOfflineTrips();
    if (results.some(({ status }) => status === "conflict")) setMessage("One offline trip needs review before it can sync. The latest online version was kept.");
  }

  async function withBusy(key: string, action: () => Promise<void>) {
    setBusyActions((existing) => [...existing, key]);
    try {
      await action();
    } finally {
      setBusyActions((existing) => existing.filter((value) => value !== key));
    }
  }

  function offlineStatuses(): readonly OfflineTripStatus[] {
    return trips.map((trip) => {
      const summary = offlineTrips.find(({ tripId }) => tripId === trip.tripId);
      return {
        tripId: trip.tripId,
        state: offlineStateFromSummary(summary),
        ...(summary?.savedAt === undefined ? {} : { savedAt: summary.savedAt }),
        queuedMutations: summary?.syncState === "pending" ? 1 : 0
      };
    });
  }

  async function createTrip(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    try {
      await apiRequest<Trip>("/v1/trips", {
        method: "POST",
        qualificationAccount,
        body: Object.fromEntries(["name", "destination", "startsOn", "endsOn"].map((name) => [name, String(data.get(name))]))
      });
      event.currentTarget.reset();
      setMessage("Trip saved. Email and calendar imports remain off in V1; add outside bookings manually.");
      await Promise.all([loadTrips(), props.refresh()]);
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "Trip could not be saved");
    }
  }

  async function saveOffline(trip: Trip) {
    try {
      const saved = await apiRequest<{ readonly savedItemIds: readonly string[]; readonly encryptedAtRestRequired: true; readonly payload: OfflineTripEssentialsPayload }>(`/v1/trips/${encodeURIComponent(trip.tripId)}/offline`, { method: "POST", qualificationAccount });
      await saveEncryptedOfflineTrip(trip.tripId, saved.payload);
      await refreshOfflineTrips();
      setMessage(`${trip.name} essentials saved in encrypted device storage (${saved.savedItemIds.length} eligible items). Live availability and purchases still require internet.`);
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "Trip could not be saved offline");
    }
  }

  async function shareTrip(trip: Trip): Promise<string | null> {
    try {
      const shared = await apiRequest<{ readonly publicPath: string }>(`/v1/trips/${encodeURIComponent(trip.tripId)}/share`, { method: "POST", qualificationAccount });
      setMessage(`View-only link created: ${globalThis.location.origin}${shared.publicPath}`);
      return shared.publicPath;
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "Trip link could not be created");
      return null;
    }
  }

  async function revokeTripShare(trip: Trip) {
    try {
      const result = await apiRequest<{ readonly revokedCount: number }>(`/v1/trips/${encodeURIComponent(trip.tripId)}/share`, { method: "DELETE", qualificationAccount });
      setMessage(result.revokedCount === 0 ? "No active view-only links were present." : `Revoked ${result.revokedCount} active view-only link${result.revokedCount === 1 ? "" : "s"}.`);
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "Trip link could not be revoked");
    }
  }

  async function duplicateTrip(trip: Trip) {
    try {
      await apiRequest<Trip>(`/v1/trips/${encodeURIComponent(trip.tripId)}/duplicate`, { method: "POST", qualificationAccount });
      setMessage(`${trip.name} duplicated without copying Portability purchase records.`);
      await Promise.all([loadTrips(), props.refresh()]);
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "Trip could not be duplicated");
    }
  }

  async function addTripItem(event: FormEvent<HTMLFormElement>, trip: Trip) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const payload = { source: String(data.get("source")), title: String(data.get("title")), localDate: String(data.get("localDate")), location: String(data.get("location")), shareLocation: data.get("shareLocation") === "on" };
    try {
      await apiRequest(`/v1/trips/${encodeURIComponent(trip.tripId)}/items`, {
        method: "POST",
        qualificationAccount,
        body: payload
      });
      event.currentTarget.reset();
      setMessage(`Manual itinerary item added to ${trip.name}.`);
      await loadTrips();
    } catch (caught) {
      try {
        await queueOfflineTripMutation(trip.tripId, { mutationId: crypto.randomUUID(), type: "manual_item_added", payload });
        event.currentTarget.reset();
        await refreshOfflineTrips();
        setMessage(`Offline change queued for ${trip.name}. It will sync to the server without being dropped when the connection returns.`);
      } catch {
        setMessage(caught instanceof Error ? caught.message : "Itinerary item could not be added");
      }
    }
  }

  async function createTripFromWorkspace(input: CreateTripInput) {
    try {
      await apiRequest<Trip>("/v1/trips", { method: "POST", qualificationAccount, body: { ...input } });
      setMessage("Trip saved. Email and calendar imports remain off in V1; add outside bookings manually.");
      await Promise.all([loadTrips(), props.refresh()]);
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "Trip could not be saved");
    }
  }

  async function updateTripFromWorkspace(tripId: string, input: UpdateTripInput) {
    try {
      await apiRequest<Trip>(`/v1/trips/${encodeURIComponent(tripId)}`, { method: "PATCH", qualificationAccount, body: input });
      setMessage("Trip changes saved.");
      await loadTrips();
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "Trip changes could not be saved");
    }
  }

  async function duplicateTripFromWorkspace(tripId: string) {
    const trip = trips.find((candidate) => candidate.tripId === tripId);
    if (trip !== undefined) await duplicateTrip(trip);
  }

  async function addTripItemFromWorkspace(tripId: string, input: AddTripItemInput) {
    const payload = {
      source: input.source ?? "MANUAL_PLAN",
      title: input.title,
      ...(input.localDate === undefined ? {} : { localDate: input.localDate }),
      ...(canonicalLocalDateTime(input.startsAt) === undefined ? {} : { startsAt: canonicalLocalDateTime(input.startsAt) }),
      ...(canonicalLocalDateTime(input.endsAt) === undefined ? {} : { endsAt: canonicalLocalDateTime(input.endsAt) }),
      ...(input.location === undefined ? {} : { location: input.location, shareLocation: false }),
      ...(input.privateNote === undefined ? {} : { privateNote: input.privateNote })
    };
    try {
      await apiRequest(`/v1/trips/${encodeURIComponent(tripId)}/items`, { method: "POST", qualificationAccount, body: payload });
      setMessage("Manual itinerary item added.");
      await loadTrips();
    } catch (caught) {
      try {
        await queueOfflineTripMutation(tripId, { mutationId: crypto.randomUUID(), type: "manual_item_added", payload });
        await refreshOfflineTrips();
        setMessage("Offline change queued. It will sync to the server without being dropped when the connection returns.");
      } catch {
        setMessage(caught instanceof Error ? caught.message : "Itinerary item could not be added");
      }
    }
  }

  async function removeTripItemFromWorkspace(tripId: string, itemId: string) {
    try {
      await apiRequest<{ readonly removed: true; readonly tripId: string; readonly itemId: string }>(`/v1/trips/${encodeURIComponent(tripId)}/items/${encodeURIComponent(itemId)}`, { method: "DELETE", qualificationAccount });
      setMessage("Itinerary item removed on the server.");
      await loadTrips();
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "Itinerary item could not be removed");
    }
  }

  async function updateTripItemFromWorkspace(tripId: string, itemId: string, input: UpdateTripItemInput) {
    const timestampPatch = (value: string | null | undefined): string | null | undefined => value === null ? null : canonicalLocalDateTime(value);
    const body: UpdateTripItemInput = {
      ...input,
      ...(input.startsAt === undefined ? {} : { startsAt: timestampPatch(input.startsAt) ?? null }),
      ...(input.endsAt === undefined ? {} : { endsAt: timestampPatch(input.endsAt) ?? null })
    };
    try {
      await apiRequest(`/v1/trips/${encodeURIComponent(tripId)}/items/${encodeURIComponent(itemId)}`, { method: "PATCH", qualificationAccount, body });
      setMessage("Itinerary item changes saved.");
      await loadTrips();
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "Itinerary item changes could not be saved");
    }
  }

  async function reorderTripItemFromWorkspace(tripId: string, orderedItemIds: readonly string[]) {
    try {
      await apiRequest(`/v1/trips/${encodeURIComponent(tripId)}/items/reorder`, { method: "PATCH", qualificationAccount, body: { orderedItemIds } });
      setMessage("Itinerary order saved.");
      await loadTrips();
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "Itinerary order could not be saved");
    }
  }

  async function shareTripFromWorkspace(tripId: string) {
    const trip = trips.find((candidate) => candidate.tripId === tripId);
    if (trip === undefined) return;
    const publicPath = await shareTrip(trip);
    if (publicPath !== null) setShareStates((existing) => ({ ...existing, [tripId]: { tripId, publicPath } }));
  }

  async function revokeTripShareFromWorkspace(tripId: string) {
    const trip = trips.find((candidate) => candidate.tripId === tripId);
    if (trip === undefined) return;
    await revokeTripShare(trip);
    setShareStates((existing) => ({ ...existing, [tripId]: { tripId, revoked: true } }));
  }

  async function saveOfflineFromWorkspace(tripId: string) {
    const trip = trips.find((candidate) => candidate.tripId === tripId);
    if (trip === undefined) return;
    await withBusy(`offline-save:${tripId}`, async () => saveOffline(trip));
  }

  async function syncOfflineFromWorkspace(tripId: string) {
    await withBusy(`offline-sync:${tripId}`, async () => {
      await syncOfflineTrips();
      setMessage(`Offline reconciliation checked for ${tripId}. Queued mutations are kept until the server confirms sync.`);
    });
  }

  async function evaluateTripPlanner(input: PlannerFormInput, acknowledgement: PlannerAcknowledgementInput) {
    await withBusy(`planner:${input.tripId}`, async () => {
      const requestedAt = new Date().toISOString();
      const result = await apiRequest<PlannerResultSummary>("/v1/planner", {
        method: "POST",
        body: {
          input: {
            requestedAt,
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            availableFrom: new Date(input.availableFrom).toISOString(),
            deadlineAt: new Date(input.deadlineAt).toISOString(),
            sourceRecords: [{ name: "traveler-entered-window", capturedAt: requestedAt, maxAgeMinutes: 5, required: true }],
            transfers: { outbound: 20, return: 20 },
            buffers: { safety: 30 },
            activities: [{ id: "activity", label: input.activity, durationMinutes: input.activityMinutes }],
            assumptions: ["Travel time entered by the traveler."],
            requireOpeningHours: false
          },
          acknowledgement: { accountId: props.bootstrap.account?.accountId ?? "signed-out-traveler", acknowledgedAt: requestedAt, statement: acknowledgement.statement }
        }
      });
      setPlannerResult(result);
      setMessage("Timing result is ready.");
    });
  }

  async function askAgenticForTrip(input: { readonly tripId: string; readonly prompt: string }) {
    await withBusy(`agentic:${input.tripId}`, async () => {
      try {
        const result = await apiRequest<AgenticTextResult>("/v1/agentic/text", {
          method: "POST",
          qualificationAccount,
          body: {
            requestId: crypto.randomUUID(),
            prompt: input.prompt,
            locale: navigator.language || "en-US",
            allowedTripIds: [input.tripId]
          }
        });
        setAgenticResult(result);
      } catch (caught) {
        setAgenticResult({
          requestId: crypto.randomUUID(),
          text: caught instanceof Error ? caught.message : "Agentic text is unavailable.",
          serverMediated: true,
          modelOutputTrustedAsAuthority: false,
          consequentialMutationOccurred: false,
          toolResults: [{ tool: "agentic.text", status: "blocked", summary: "Provider or account configuration is not available." }]
        });
      }
    });
  }

  if (props.bootstrap.account === null) return <section aria-labelledby="trips-title"><div className="section-heading"><div><span className="eyebrow">{copy("nav.trips").toUpperCase()}</span><h2 id="trips-title">{text("Your trips and offline essentials", { es: "Tus viajes y esenciales sin conexión", fr: "Vos voyages et essentiels hors ligne" })}</h2></div><p>{text("Browse without an account. Sign in when cloud trips, bookings, or sync are needed.", { es: "Explora sin cuenta. Inicia sesión cuando necesites viajes en la nube, reservas o sincronización.", fr: "Explorez sans compte. Connectez-vous lorsque les voyages cloud, réservations ou synchronisation sont nécessaires." })}</p></div><section className="card"><p>{text("Passwords are never created. Any encrypted offline essentials already saved on this device remain reachable without a network session.", { es: "Nunca se crean contraseñas. Los esenciales cifrados ya guardados en este dispositivo siguen disponibles sin una sesión de red.", fr: "Aucun mot de passe n’est créé. Les essentiels hors ligne chiffrés déjà enregistrés sur cet appareil restent accessibles sans session réseau." })}</p>{props.liveServicesAvailable ? <button className="primary" type="button" onClick={() => props.onNavigate("profile")}>{copy("auth.signIn")} {text("to", { es: "a", fr: "à" })} {copy("trip.create").toLowerCase()}</button> : <p className="limited-mode">{text("Cloud trips and sign-in will activate when Portability’s live service is connected. Nothing has been lost from this device.", { es: "Los viajes en la nube y el inicio de sesión se activarán cuando el servicio en vivo de Portability esté conectado. Nada se perdió de este dispositivo.", fr: "Les voyages cloud et la connexion s’activeront lorsque le service en direct de Portability sera connecté. Rien n’a été perdu sur cet appareil." })}</p>}{message && <p className="notice" aria-live="polite">{message}</p>}</section>{offlineTrips.length > 0 && <section className="section card" aria-labelledby="offline-title"><div className="section-heading"><div><span className="eyebrow">{text("OFFLINE", { es: "SIN CONEXIÓN", fr: "HORS LIGNE" })}</span><h3 id="offline-title">{copy("trip.offlineEssentials")}</h3></div><p>{text("Read-only while offline. Live availability, purchases, changes, sharing, and cloud sync stay online-only.", { es: "Solo lectura sin conexión. Inventario en vivo, compras, cambios de proveedor, compartir y sincronización en la nube siguen solo en línea.", fr: "Lecture seule hors ligne. Inventaire en direct, achats, changements fournisseur, partage et synchronisation cloud restent en ligne uniquement." })}</p></div><div className="grid">{offlineTrips.map((trip) => <article className="card" key={trip.tripId}><h3>{text("Saved trip", { es: "Viaje guardado", fr: "Voyage enregistré" })}</h3><p>{text("Saved", { es: "Guardado", fr: "Enregistré" })} {trip.savedAt || text("on this device", { es: "en este dispositivo", fr: "sur cet appareil" })}. {offlineSyncLabel(trip.syncState)}.</p><div className="button-row"><button className="secondary" type="button" onClick={() => void viewOfflineTrip(trip.tripId)}>{text("Open offline", { es: "Abrir sin conexión", fr: "Ouvrir hors ligne" })}</button><button className="secondary" type="button" onClick={() => void removeOfflineTrip(trip.tripId)}>{text("Delete offline", { es: "Eliminar sin conexión", fr: "Supprimer hors ligne" })}</button></div></article>)}</div>{offlineDetail && <div className="notice"><strong>{offlineDetail.trip.name}</strong><p>{offlineDetail.trip.destination} {text("from", { es: "desde", fr: "de" })} {offlineDetail.trip.startsOn} {text("to", { es: "a", fr: "à" })} {offlineDetail.trip.endsOn}</p><ul>{offlineDetail.items.map((item) => <li key={item.itemId}>{item.title}</li>)}</ul></div>}</section>}</section>;
  return <section aria-labelledby="trips-title">
    <div className="section-heading"><div><span className="eyebrow">{copy("nav.trips").toUpperCase()}</span><h2 id="trips-title">{text("Your itinerary, one day at a time", { es: "Tu itinerario, un día a la vez", fr: "Votre itinéraire, un jour à la fois" })}</h2></div><p>{online ? text("No separate Now screen. Current guidance stays here.", { es: "No hay pantalla Ahora separada. La guía actual permanece aquí.", fr: "Aucun écran Maintenant séparé. Les conseils actuels restent ici." }) : text("Offline mode: encrypted essentials on this device remain readable.", { es: "Modo sin conexión: los esenciales cifrados de este dispositivo siguen legibles.", fr: "Mode hors ligne : les essentiels chiffrés sur cet appareil restent lisibles." })}</p></div>
    {message && <p className="notice" aria-live="polite">{message}</p>}
    <TripWorkspace
      trips={trips}
      selectedTripId={selectedTripId}
      itemsByTripId={itemsByTripId}
      offlineStatuses={offlineStatuses()}
      shareStates={shareStates}
      plannerResult={plannerResult}
      agenticResult={agenticResult}
      now={new Date().toISOString()}
      locale={props.locale}
      busyActions={busyActions}
      onSelectTrip={setSelectedTripId}
      onCreateTrip={(input) => void createTripFromWorkspace(input)}
      onUpdateTrip={(tripId, input) => void updateTripFromWorkspace(tripId, input)}
      onDuplicateTrip={(tripId) => void duplicateTripFromWorkspace(tripId)}
      onAddItem={(tripId, input) => void addTripItemFromWorkspace(tripId, input)}
      onUpdateItem={(tripId, itemId, input) => void updateTripItemFromWorkspace(tripId, itemId, input)}
      onReorderItem={(tripId, orderedItemIds) => void reorderTripItemFromWorkspace(tripId, orderedItemIds)}
      onRemoveItem={(tripId, itemId) => void removeTripItemFromWorkspace(tripId, itemId)}
      onShareTrip={(tripId) => void shareTripFromWorkspace(tripId)}
      onRevokeShare={(tripId) => void revokeTripShareFromWorkspace(tripId)}
      onSaveOffline={(tripId) => void saveOfflineFromWorkspace(tripId)}
      onSyncOffline={(tripId) => void syncOfflineFromWorkspace(tripId)}
      onEvaluatePlanner={(input, acknowledgement) => void evaluateTripPlanner(input, acknowledgement)}
      onAskAgentic={(input) => void askAgenticForTrip(input)}
    />
    {offlineTrips.length > 0 && <section className="section card" aria-labelledby="offline-title"><div className="section-heading"><div><span className="eyebrow">{text("OFFLINE", { es: "SIN CONEXIÓN", fr: "HORS LIGNE" })}</span><h3 id="offline-title">{text("Offline essentials on this device", { es: "Esenciales sin conexión en este dispositivo", fr: "Essentiels hors ligne sur cet appareil" })}</h3></div><p>{text("Read-only while offline. Live availability, purchases, changes, sharing, and cloud sync stay online-only.", { es: "Solo lectura sin conexión. Inventario en vivo, compras, cambios de proveedor, compartir y sincronización en la nube siguen solo en línea.", fr: "Lecture seule hors ligne. Inventaire en direct, achats, changements fournisseur, partage et synchronisation cloud restent en ligne uniquement." })}</p></div><div className="grid">{offlineTrips.map((trip) => <article className="card" key={trip.tripId}><h3>{text("Saved trip", { es: "Viaje guardado", fr: "Voyage enregistré" })}</h3><p>{text("Saved", { es: "Guardado", fr: "Enregistré" })} {trip.savedAt || text("on this device", { es: "en este dispositivo", fr: "sur cet appareil" })}. {offlineSyncLabel(trip.syncState)}.</p><div className="button-row"><button className="secondary" type="button" onClick={() => void viewOfflineTrip(trip.tripId)}>{text("Open offline", { es: "Abrir sin conexión", fr: "Ouvrir hors ligne" })}</button><button className="secondary" type="button" onClick={() => void removeOfflineTrip(trip.tripId)}>{text("Delete offline", { es: "Eliminar sin conexión", fr: "Supprimer hors ligne" })}</button></div></article>)}</div>{offlineDetail && <div className="notice"><strong>{offlineDetail.trip.name}</strong><p>{offlineDetail.trip.destination} {text("from", { es: "desde", fr: "de" })} {offlineDetail.trip.startsOn} {text("to", { es: "a", fr: "à" })} {offlineDetail.trip.endsOn}</p><ul>{offlineDetail.items.map((item) => <li key={item.itemId}>{item.title}</li>)}</ul></div>}</section>}
  </section>;
}

function Live(props: { readonly bootstrap: Bootstrap; readonly locale: RequiredLocaleCode }) {
  const copy = (key: PortabilityMessageKey) => localizedMessage(props.locale, key);
  const canHost = props.bootstrap.entitlement?.hostLivestreams === true;
  const [message, setMessage] = useState("No live or scheduled streams yet.");
  const [streams, setStreams] = useState<readonly CreatorStreamSummary[]>([]);
  const [selectedStreamId, setSelectedStreamId] = useState<string | null>(null);
  const liveCapability = props.bootstrap.providerCapabilities.find(({ category, actionable, providers }) => (category === "creator_live" || category === "livestreams") && actionable && providers.length > 0);
  const liveProviderAvailable = liveCapability !== undefined;
  useEffect(() => {
    void apiRequest<{ readonly streams: readonly CreatorStreamSummary[] }>("/v1/community/streams", { qualificationAccount })
      .then(({ streams }) => {
        const safeStreams = Array.isArray(streams) ? streams : [];
        setStreams(safeStreams);
        setMessage(safeStreams.length === 0 ? "No live or scheduled streams yet." : `${safeStreams.length} stream${safeStreams.length === 1 ? "" : "s"} available.`);
      })
      .catch((error: unknown) => {
        setStreams([]);
        setMessage(error instanceof Error ? error.message : "Streams could not be loaded right now.");
      });
  }, []);
  const selectedStream = streams.find(({ streamId }) => streamId === selectedStreamId) ?? streams[0] ?? null;
  const publicCreators = useMemo(() => Array.from(new Map(streams.map((stream) => [stream.creatorAccountId, stream])).values()).map((stream) => ({
    creatorAccountId: stream.creatorAccountId,
    handle: stream.creatorDisplayName.toLowerCase().replace(/[^a-z0-9]+/gu, "-").replace(/^-|-$/gu, "") || stream.creatorAccountId,
    displayName: stream.creatorDisplayName,
    bio: stream.locationLabel ? `${stream.category} · ${stream.locationLabel}` : stream.category,
    followerCount: 0,
    followed: false,
    liveCount: streams.filter((candidate) => candidate.creatorAccountId === stream.creatorAccountId && candidate.status === "LIVE").length
  })), [streams]);
  function liveDiscoverySection(mode: CreatorDiscoveryMode): "LIVE_NOW" | "FOLLOWED" | "TRENDING" | "LOCATION" | "CATEGORY" | "SEARCH" | null {
    if (mode === "live") return "LIVE_NOW";
    if (mode === "followed") return "FOLLOWED";
    if (mode === "trending") return "TRENDING";
    if (mode === "location") return "LOCATION";
    if (mode === "category") return "CATEGORY";
    if (mode === "search") return "SEARCH";
    return null;
  }
  async function discoverLiveStreams(input: { readonly mode: CreatorDiscoveryMode; readonly category: string | null; readonly locationBucket: string | null; readonly search: string | null }) {
    const params = new URLSearchParams();
    const section = liveDiscoverySection(input.mode);
    if (section !== null) params.set("section", section);
    if (input.mode === "upcoming") params.set("status", "SCHEDULED");
    if (input.category !== null) params.set("category", input.category);
    if (input.locationBucket !== null) params.set("locationBucket", input.locationBucket);
    if (input.search !== null) params.set("search", input.search);
    const value = await apiRequest<{ readonly streams: readonly CreatorStreamSummary[] }>(`/v1/community/streams${params.size === 0 ? "" : `?${params.toString()}`}`, { qualificationAccount });
    const nextStreams = Array.isArray(value.streams) ? value.streams : [];
    setStreams(nextStreams);
    setSelectedStreamId(nextStreams[0]?.streamId ?? null);
    setMessage(nextStreams.length === 0 ? "No streams match those filters." : `${nextStreams.length} stream${nextStreams.length === 1 ? "" : "s"} found.`);
  }
  async function liveMutation(path: string, body?: Record<string, unknown>, method: "POST" | "PATCH" | "DELETE" = "POST") {
    await apiRequest(path, { method, qualificationAccount, ...(body === undefined ? {} : { body }) });
  }
  async function recordLiveAnalytics(streamId: string, event: "STREAM_VIEW" | "PLAYBACK_START" | "FOLLOW" | "CHAT_MESSAGE" | "OFFER_CLICK" | "TIP_CLICK", campaignId?: string | null) {
    await liveMutation(`/v1/community/streams/${encodeURIComponent(streamId)}/analytics`, { event, campaignId: campaignId ?? null });
    setMessage("Privacy-safe stream analytics recorded.");
  }
  async function createBroadcast(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (liveCapability === undefined) return;
    const data = new FormData(event.currentTarget);
    try {
    const result = await apiRequest<{ readonly result: { readonly streamId: string; readonly productionStreamFabricated: false } }>("/v1/features/creator/broadcast/create", {
        method: "POST",
        qualificationAccount,
        body: { provider: liveCapability.providers[0], title: String(data.get("title")), visibility: String(data.get("visibility")), category: String(data.get("category")), startsAt: new Date(String(data.get("startsAt"))).toISOString() }
      });
      setMessage("Your broadcast is scheduled. It will appear after playback and safety checks are complete.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Broadcast could not be created");
    }
  }
  return <section aria-labelledby="live-title">
    <div className="section-heading"><div><span className="eyebrow">{copy("nav.live").toUpperCase()}</span><h2 id="live-title">Creators, places, and experiences in real time</h2></div></div>
    {canHost && <form className="card form" onSubmit={createBroadcast}><h3>Schedule a broadcast</h3><Field label="Title" name="title" required /><Field label="Category" name="category" required defaultValue="travel" /><Field label="Starts" name="startsAt" type="datetime-local" required /><Field label="Who can watch" name="visibility"><select name="visibility" defaultValue="PUBLIC"><option value="PUBLIC">Everyone</option><option value="FOLLOWERS">Followers</option><option value="PRIVATE">Private</option></select></Field><button className="primary" type="submit" disabled={!liveProviderAvailable} title={liveProviderAvailable ? "Schedule broadcast" : "Broadcasting is not available yet"}>Schedule</button>{!liveProviderAvailable && <p className="microcopy">Broadcasting is not available yet. You can still watch public streams.</p>}</form>}
    <div className="card"><h3>Live and scheduled</h3><p className="notice" aria-live="polite">{message}</p><p>Public streams appear after playback and safety checks are complete.</p></div>
    {selectedStream !== null && <MobileStoreCreatorTips bootstrap={props.bootstrap} streamId={selectedStream.streamId} />}
    <CreatorCommunityPanel
      currentAccountId={props.bootstrap.account?.accountId ?? null}
      creatorToolsEnabled={canHost}
      creators={publicCreators}
      streams={streams}
      selectedStream={selectedStream}
      chatMessages={[]}
      replays={[]}
      campaigns={[]}
      capabilities={[{ key: "creator_live", label: "Creator Live", state: liveCapability === undefined ? "EXTERNAL_BLOCKER" : liveCapability.state === "READY" ? "READY" : "SANDBOX_ONLY", reason: liveCapability === undefined ? "Live playback is not available yet." : "Live playback is available after safety checks." }]}
      locale={props.locale}
      callbacks={{
        onFollowCreator: async (creatorAccountId) => {
          await liveMutation(`/v1/community/creators/${encodeURIComponent(creatorAccountId)}/follow`);
          setMessage("Creator followed.");
        },
        onOpenStream: (streamId) => { setSelectedStreamId(streamId); setMessage("Stream selected."); },
        onDiscoverStreams: discoverLiveStreams,
        onRecordStreamAnalytics: ({ streamId, event, campaignId }) => recordLiveAnalytics(streamId, event, campaignId),
        onReportStream: async ({ streamId, reason }) => {
          await liveMutation(`/v1/community/streams/${encodeURIComponent(streamId)}/report`, { reason });
          setMessage("Stream report submitted.");
        },
        onBlockCreator: async (creatorAccountId) => {
          await liveMutation(`/v1/community/creators/${encodeURIComponent(creatorAccountId)}/block`);
          setStreams((existing) => existing.filter((stream) => stream.creatorAccountId !== creatorAccountId));
          setMessage("Creator blocked.");
        },
        onMuteStream: async (streamId) => {
          await liveMutation(`/v1/community/streams/${encodeURIComponent(streamId)}/mute`);
          setMessage("Stream muted for this account.");
        },
        onSendChat: async ({ streamId, text }) => {
          await liveMutation(`/v1/community/streams/${encodeURIComponent(streamId)}/chat`, { text });
          setMessage("Chat submitted for moderation.");
        },
        onModerateChat: async ({ messageId, hide, reason }) => {
          await liveMutation(`/v1/community/chat/${encodeURIComponent(messageId)}/moderate`, { hide, reason });
          setMessage("Chat moderation updated.");
        },
        onSetReplayVisibility: async ({ replayId, visibility }) => {
          await liveMutation(`/v1/community/replays/${encodeURIComponent(replayId)}/privacy`, { visibility }, "PATCH");
          setMessage("Replay visibility updated.");
        },
        onOpenReplay: () => setMessage("Replay selected."),
        onDeleteReplay: async (replayId) => {
          await liveMutation(`/v1/community/replays/${encodeURIComponent(replayId)}`, undefined, "DELETE");
          setMessage("Replay deleted.");
        },
        onCreateCampaign: async (input) => {
          await liveMutation("/v1/community/campaigns", input as unknown as Record<string, unknown>);
          setMessage("Creator campaign created.");
        },
        onArchiveCampaign: async (campaignId) => {
          await liveMutation(`/v1/community/campaigns/${encodeURIComponent(campaignId)}/archive`);
          setMessage("Creator campaign archived.");
        }
      }}
    />
    <div className="section grid"><article className="card"><h3>Creator access</h3><p>{canHost ? "Your plan includes hosting after identity and safety checks are complete." : "Creator plans can host broadcasts. Everyone can watch public streams."}</p></article></div>
  </section>;
}

function MobileStoreCreatorTips(props: { readonly bootstrap: Bootstrap; readonly streamId: string }) {
  const [products, setProducts] = useState<readonly MobileStoreProductDetails[]>([]);
  const [busy, setBusy] = useState(false);
  const storeName = mobileStoreName();
  const platform = deviceStorePlatform();
  const [message, setMessage] = useState(`${storeName} tip prices appear only when the approved products are available.`);
  const account = props.bootstrap.account;
  const billingProvider = mobileBillingProvider(props.bootstrap.providerCapabilities);
  useEffect(() => {
    if (!mobileStoreBillingAvailable() || account === null || billingProvider === null) return;
    void queryMobileStoreOneTimeProducts()
      .then((items) => setProducts(items.filter(({ id }) => id.includes(".creator.tip."))))
      .catch((error: unknown) => setMessage(error instanceof Error ? error.message : `${storeName} tip products could not be loaded`));
  }, [account, billingProvider, storeName]);
  async function tip(product: MobileStoreProductDetails, streamId: string) {
    if (account === null || billingProvider === null) return;
    setBusy(true);
    try {
      const restored = await restoreMobileStorePurchases(account.accountId, props.bootstrap.mobileStoreAccountToken, qualificationAccount);
      const recoveredPurchase = restored.find((item) => item.productId === product.id);
      const submissions = recoveredPurchase === undefined
        ? await purchaseMobileStoreProduct(account.accountId, props.bootstrap.mobileStoreAccountToken, product.id, qualificationAccount)
        : restored;
      const purchase = submissions.find((item) => item.productId === product.id);
      if (purchase === undefined || purchase.status === "PENDING") {
        setMessage(`${storeName} is still processing this tip. It will be routed after purchase verification completes.`);
        return;
      }
      if (!purchase.purchaseActivityId) throw new Error("The verified mobile-store purchase activity is missing");
      await apiRequest("/v1/features/creator/tip/classify", {
        method: "POST",
        qualificationAccount,
        body: { storeProvider: billingProvider, clientPlatform: platform === "APPLE" ? "IOS" : "ANDROID", purchaseContext: "MOBILE_DIGITAL_TIP", productId: product.id, streamId, storePurchaseActivityId: purchase.purchaseActivityId, idempotencyKey: crypto.randomUUID() }
      });
      await finalizeMobileStoreConsumablePurchase(purchase, qualificationAccount);
      setMessage(`${storeName} verified and routed the tip. Creator settlement will update after final account checks.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "The Creator tip could not be completed");
    } finally {
      setBusy(false);
    }
  }
  if (!mobileStoreBillingAvailable() || account === null) return null;
  return <section className="card" aria-labelledby="creator-tips-title"><h3 id="creator-tips-title">Tip this Creator</h3><p className="notice" aria-live="polite">{message}</p><form className="form" onSubmit={(event) => { event.preventDefault(); const data = new FormData(event.currentTarget); const product = products.find(({ id }) => id === String(data.get("productId"))); if (product !== undefined) void tip(product, props.streamId); }}><Field label="Tip amount" name="productId"><select name="productId" required defaultValue=""> <option value="" disabled>Select a {storeName} price</option>{products.map((product) => <option value={product.id} key={product.id}>{product.formattedPrice ?? product.name}</option>)}</select></Field><button className="primary" type="submit" disabled={busy || billingProvider === null || products.length === 0}>Tip through {storeName}</button></form><p className="microcopy">Your tip is credited only after {storeName} confirms the purchase.</p></section>;
}

function AuthenticationControls(props: { readonly liveServicesAvailable: boolean; readonly locale: RequiredLocaleCode }) {
  const copy = (key: PortabilityMessageKey) => localizedMessage(props.locale, key);
  const [methods, setMethods] = useState<readonly AuthMethod[]>([]);
  const authStartInFlightRef = useRef(false);
  const emailCooldownUntilRef = useRef(0);
  const [pendingProvider, setPendingProvider] = useState<AuthMethod["provider"] | null>(null);
  const [emailCooldownSeconds, setEmailCooldownSeconds] = useState(0);
  const [message, setMessage] = useState(authResultWasAcknowledged() ? (props.liveServicesAvailable ? "Loading available sign-in methods…" : "Sign-in will appear when Portability’s live service is connected. You can continue browsing without an account.") : latestAuthResult ?? (props.liveServicesAvailable ? "Loading available sign-in methods…" : "Sign-in will appear when Portability’s live service is connected. You can continue browsing without an account."));
  useEffect(() => {
    if (!props.liveServicesAvailable) {
      setMethods([]);
      setMessage("Sign-in will appear when Portability’s live service is connected. You can continue browsing without an account.");
      return;
    }
    void apiRequest<{ readonly methods: readonly AuthMethod[] }>("/v1/auth/methods")
      .then((result) => {
        setMethods(result.methods);
        setMessage(result.methods.some(({ available }) => available) ? "Choose how you want to continue." : "Sign-in is not available right now. You can continue browsing without an account.");
      })
      .catch(() => setMessage("Sign-in is temporarily unavailable. You can continue browsing without an account."));
  }, [props.liveServicesAvailable]);
  useEffect(() => {
    const receive = (event: Event) => setMessage((event as CustomEvent<string>).detail);
    globalThis.addEventListener(AUTH_RESULT_EVENT, receive);
    return () => globalThis.removeEventListener(AUTH_RESULT_EVENT, receive);
  }, []);
  useEffect(() => {
    if (emailCooldownSeconds <= 0) return undefined;
    const timer = globalThis.setInterval(() => setEmailCooldownSeconds((seconds) => Math.max(0, seconds - 1)), 1000);
    return () => globalThis.clearInterval(timer);
  }, [emailCooldownSeconds]);

  async function begin(provider: AuthMethod["provider"], email: string | null) {
    if (authStartInFlightRef.current) return;
    const remainingEmailCooldownSeconds = Math.max(emailCooldownSeconds, Math.ceil((emailCooldownUntilRef.current - Date.now()) / 1000));
    if (provider === "email_magic_link" && remainingEmailCooldownSeconds > 0) {
      setEmailCooldownSeconds(remainingEmailCooldownSeconds);
      setMessage(`Check your email. We already sent a secure link. You can request another link in ${remainingEmailCooldownSeconds} second${remainingEmailCooldownSeconds === 1 ? "" : "s"}.`);
      return;
    }
    authStartInFlightRef.current = true;
    setPendingProvider(provider);
    setMessage(provider === "email_magic_link" ? "Sending a secure email link…" : "Opening secure sign-in…");
    try {
      const result = await apiRequest<{ readonly authorizationUrl: string | null; readonly expiresAt: string }>("/v1/auth/start", {
        method: "POST",
        body: { provider, email, callbackUrl: authCallbackUrl() }
      });
      if (result.authorizationUrl !== null) globalThis.location.assign(result.authorizationUrl);
      else {
        if (provider === "email_magic_link") {
          emailCooldownUntilRef.current = Date.now() + 15000;
          setEmailCooldownSeconds(15);
          setMessage("Check your email. We sent one secure link to continue to Portability. Wait a few seconds before requesting another link.");
        } else {
          setMessage("Continue in the secure sign-in window, then return to Portability.");
        }
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Sign-in could not be started");
    } finally {
      authStartInFlightRef.current = false;
      setPendingProvider(null);
    }
  }

  const available = new Set(methods.filter((method) => method.available).map((method) => method.provider));
  const signInPending = pendingProvider !== null;
  const emailSubmitDisabled = signInPending || emailCooldownSeconds > 0;
  return <article className="card"><h2>{copy("auth.passwordless")}</h2><p className="notice" aria-live="polite">{message}</p><div className="button-row">{available.has("apple") && <button className="secondary" type="button" disabled={signInPending} onClick={() => void begin("apple", null)}>{pendingProvider === "apple" ? "Opening Apple…" : copy("auth.continueApple")}</button>}{available.has("google") && <button className="secondary" type="button" disabled={signInPending} onClick={() => void begin("google", null)}>{pendingProvider === "google" ? "Opening Google…" : copy("auth.continueGoogle")}</button>}</div>{available.has("email_magic_link") && <form className="form" onSubmit={(event) => { event.preventDefault(); if (emailSubmitDisabled) return; void begin("email_magic_link", String(new FormData(event.currentTarget).get("email"))); }}><p>{copy("auth.noPasswords")}</p><Field label={copy("auth.emailMagicLink")} name="email" type="email" autoComplete="email" required disabled={signInPending} /><button className="secondary" type="submit" disabled={emailSubmitDisabled}>{pendingProvider === "email_magic_link" ? "Sending link…" : emailCooldownSeconds > 0 ? `Link sent — wait ${emailCooldownSeconds}s` : copy("auth.emailMagicLinkButton")}</button></form>}</article>;
}

function MemberTools(props: { readonly bootstrap: Bootstrap; readonly locale: RequiredLocaleCode; readonly focus: "rewards" | "family"; readonly onParentSessionChange?: (parentSessionId: string | null) => void }) {
  const copy = (key: PortabilityMessageKey) => localizedMessage(props.locale, key);
  const [activity, setActivity] = useState<readonly { readonly activityId: string; readonly kind: string; readonly status: string; readonly amountMinor: number | null; readonly currency: string | null }[]>([]);
  const [message, setMessage] = useState("Loading account-backed activity…");
  const [parentSessionId, setParentSessionId] = useState<string | null>(null);
  const [kidsProduct, setKidsProduct] = useState<MobileStoreProductDetails | null>(null);
  const [kidsBusy, setKidsBusy] = useState(false);
  const storeName = mobileStoreName();
  const translationCapability = props.bootstrap.providerCapabilities.find(({ category, actionable, providers }) => category === "translation" && actionable && providers.length > 0);
  const billingProvider = mobileBillingProvider(props.bootstrap.providerCapabilities);
  const payoutCapability = props.bootstrap.providerCapabilities.find(({ category, actionable, providers }) => category === "payout" && actionable && providers.length > 0);
  const kidsCapability = props.bootstrap.providerCapabilities.find(({ category, actionable, providers }) => category === "kids_media" && actionable && providers.length > 0);
  useEffect(() => {
    void apiRequest<{ readonly result: { readonly events: readonly typeof activity[number][]; readonly fabricatedSavingsIncluded: false } }>("/v1/features/activity/read", { qualificationAccount })
      .then(({ result }) => { setActivity(result.events); setMessage(result.events.length === 0 ? "No verified account activity yet." : `${result.events.length} verified account events.`); })
      .catch((error: unknown) => setMessage(error instanceof Error ? error.message : "Activity could not be loaded"));
  }, []);
  useEffect(() => {
    if (!mobileStoreBillingAvailable() || billingProvider === null) return;
    void queryMobileStoreOneTimeProducts()
      .then((products) => setKidsProduct(products.find(({ id }) => id === "com.portability.app.kids.download") ?? null))
      .catch((error: unknown) => setMessage(error instanceof Error ? error.message : `Kid’s Corner ${storeName} product could not be loaded`));
  }, [billingProvider, storeName]);

  async function submitPayoutPreference(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    try {
      const response = await apiRequest<{ readonly result: { readonly effectiveAtNextWeeklyCutoff: true } }>("/v1/features/payout/preference/set", { method: "POST", qualificationAccount, body: { method: String(data.get("method")), destinationReference: String(data.get("destinationReference")) } });
      setMessage(response.result.effectiveAtNextWeeklyCutoff ? "Fiat payout preference verified and scheduled for the next weekly cutoff." : "Payout preference was not changed.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Payout preference could not be changed");
    }
  }

  async function requestTranslation(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (translationCapability === undefined) return;
    const data = new FormData(event.currentTarget);
    try {
      const response = await apiRequest<{ readonly result: { readonly targetLocale: string; readonly providerTranslationFabricated: false } }>("/v1/features/agentic/translation/request", { method: "POST", qualificationAccount, body: { provider: translationCapability.providers[0], text: String(data.get("text")), sourceLocale: String(data.get("sourceLocale")), targetLocale: String(data.get("targetLocale")) } });
      setMessage(`Translation request accepted for ${response.result.targetLocale}. The result will appear when it is ready.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Translation request could not be created");
    }
  }

  async function createParentPin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    try {
      await apiRequest("/v1/features/kids/parent_pin/create", { method: "POST", qualificationAccount, body: { pin: String(new FormData(form).get("pin")) } });
      form.reset();
      setMessage("Parent PIN created.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Parent PIN could not be created");
    }
  }

  async function openParentSession(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    try {
      const response = await apiRequest<{ readonly result: { readonly sessionId: string } }>("/v1/features/kids/parent_session/open", { method: "POST", qualificationAccount, body: { pin: String(new FormData(form).get("pin")) } });
      setParentSessionId(response.result.sessionId);
      props.onParentSessionChange?.(response.result.sessionId);
      form.reset();
      setMessage("Parent controls are open for this session.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Parent controls could not be opened");
    }
  }

  async function authorizeKidsDownload() {
    if (parentSessionId === null) return;
    setKidsBusy(true);
    try {
      let storePurchaseActivityId: string | null = null;
      let verifiedStorePurchase: Awaited<ReturnType<typeof purchaseMobileStoreProduct>>[number] | null = null;
      if (props.bootstrap.entitlement?.tier === "FREE") {
        if (!mobileStoreBillingAvailable() || kidsProduct === null || billingProvider === null) throw new Error(`The verified ${storeName} Kid’s Corner purchase is not available yet`);
        const restored = await restoreMobileStorePurchases(props.bootstrap.account!.accountId, props.bootstrap.mobileStoreAccountToken, qualificationAccount);
        const recoveredPurchase = restored.find(({ productId }) => productId === kidsProduct.id);
        const submissions = recoveredPurchase === undefined
          ? await purchaseMobileStoreProduct(props.bootstrap.account!.accountId, props.bootstrap.mobileStoreAccountToken, kidsProduct.id, qualificationAccount)
          : restored;
        const purchase = submissions.find(({ productId }) => productId === kidsProduct.id);
        if (purchase === undefined || purchase.status === "PENDING") {
          setMessage(`${storeName} is still processing the Kid’s Corner purchase. No download was authorized yet.`);
          return;
        }
        storePurchaseActivityId = purchase.purchaseActivityId;
        verifiedStorePurchase = purchase;
      }
      await apiRequest("/v1/features/kids/download/authorize", {
        method: "POST",
        qualificationAccount,
        body: {
          parentSessionId,
          period: new Date().toISOString().slice(0, 7),
          ...(storePurchaseActivityId === null ? {} : { provider: billingProvider!, currency: "USD", storePurchaseActivityId }),
          idempotencyKey: crypto.randomUUID()
        }
      });
      if (verifiedStorePurchase !== null) await finalizeMobileStoreConsumablePurchase(verifiedStorePurchase, qualificationAccount);
      setMessage(storePurchaseActivityId === null ? "An included Kid’s Corner download was authorized." : `${storeName} verified the purchase and authorized one Kid’s Corner download.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Kid’s Corner download could not be authorized");
    } finally {
      setKidsBusy(false);
    }
  }

  return <section className="section" aria-labelledby="member-tools-title"><div className="section-heading"><div><span className="eyebrow">{props.focus === "rewards" ? "REWARDS" : "FAMILY"}</span><h3 id="member-tools-title">{props.focus === "rewards" ? "Activity and payouts" : "Translation and parent controls"}</h3></div></div><p className="notice" aria-live="polite">{message}</p><div className="grid">{props.focus === "rewards" ? <><article className="card"><h3>My Activity</h3>{activity.length === 0 ? <p>{copy("state.noActivity")}</p> : <ul>{activity.map((event) => <li key={event.activityId}>{event.kind.replaceAll("_", " ").toLowerCase()}: {event.status.replaceAll("_", " ").toLowerCase()}{event.amountMinor === null ? "" : ` — ${formatLocalizedCurrency(props.locale, event.amountMinor, event.currency ?? "USD")}`}</li>)}</ul>}</article><article className="card"><h3>{copy("mobile.walletPayouts")}</h3><form className="form" onSubmit={submitPayoutPreference}><Field label="Payout method" name="method"><select name="method" defaultValue="RLUSD"><option value="RLUSD">RLUSD</option><option value="XRP">XRP</option><option value="FIAT">USD, where supported</option></select></Field><Field label="Wallet or payout destination" name="destinationReference" required /><button className="secondary" type="submit" disabled={payoutCapability === undefined}>Save for next payout</button></form><p>{payoutCapability === undefined ? "Payout setup is not available yet. Eligible earnings remain protected until identity and tax checks are complete." : "Changes take effect at the next weekly payout."}</p></article></> : <><article className="card"><h3>Translation</h3><form className="form" onSubmit={requestTranslation}><Field label="Text" name="text"><textarea name="text" required /></Field><Field label="From" name="sourceLocale" defaultValue="en" required /><Field label="To" name="targetLocale" defaultValue="es" required /><button className="secondary" type="submit" disabled={translationCapability === undefined}>Translate</button></form>{translationCapability === undefined && <p>Translation is not available yet.</p>}</article><article className="card"><h3>Kid's Corner</h3><form className="form" onSubmit={createParentPin}><Field label="New parent PIN" name="pin" type="password" required /><button className="secondary" type="submit" disabled={kidsCapability === undefined}>Set parent PIN</button></form><form className="form" onSubmit={openParentSession}><Field label="Parent PIN" name="pin" type="password" required /><button className="secondary" type="submit" disabled={kidsCapability === undefined}>Open parent controls</button></form><button className="primary" type="button" disabled={kidsCapability === undefined || parentSessionId === null || kidsBusy || (props.bootstrap.entitlement?.tier === "FREE" && (kidsProduct === null || billingProvider === null))} onClick={() => void authorizeKidsDownload()}>{props.bootstrap.entitlement?.tier === "FREE" ? `Buy one download${kidsProduct?.formattedPrice ? ` — ${kidsProduct.formattedPrice}` : ""}` : "Use one included download"}</button><p>{kidsCapability === undefined ? "Kid's Corner is not available yet." : `Child profiles, purchases, social features, Live, wallet, and adult data stay behind parent controls. Free downloads use ${storeName} and open only after purchase confirmation.`}</p></article></>}</div></section>;
}

function MobileStoreMembershipBilling(props: { readonly account: Account; readonly appAccountToken: string | null; readonly providerCapabilities: Bootstrap["providerCapabilities"]; readonly locale: RequiredLocaleCode; readonly refresh: () => Promise<void> }) {
  const copy = (key: PortabilityMessageKey) => localizedMessage(props.locale, key);
  const [products, setProducts] = useState<readonly MobileStoreProductDetails[]>([]);
  const [busy, setBusy] = useState(false);
  const storeName = mobileStoreName();
  const [message, setMessage] = useState(`Connect to ${storeName} to view current membership prices.`);
  const billingProvider = mobileBillingProvider(props.providerCapabilities);
  const available = mobileStoreBillingAvailable() && billingProvider !== null;

  useEffect(() => {
    if (!available) return;
    void queryMobileStoreMembershipProducts()
      .then((catalog) => {
        setProducts(catalog);
        setMessage(catalog.length === 0 ? `${storeName} memberships are not active for this tester account.` : `Prices and renewal terms come directly from ${storeName}.`);
      })
      .catch((error: unknown) => setMessage(error instanceof Error ? error.message : `${storeName} products could not be loaded`));
  }, [available]);

  useEffect(() => {
    if (!available) return;
    let disposed = false;
    let handle: { remove(): Promise<void> } | null = null;
    void watchMobileStorePurchaseUpdates(
      props.account.accountId,
      props.appAccountToken,
      qualificationAccount,
      (results) => {
        if (disposed || results.length === 0) return;
        setMessage(results.some(({ status }) => status === "PENDING")
          ? `${storeName} is still processing this purchase.`
          : `${storeName} completed the purchase and Portability verified the membership.`);
        void props.refresh();
      },
      (error) => {
        if (!disposed) setMessage(error instanceof Error ? error.message : `A ${storeName} purchase update could not be verified`);
      }
    ).then((value) => {
      if (disposed) void value?.remove();
      else handle = value;
    });
    return () => {
      disposed = true;
      void handle?.remove();
    };
  }, [available, props.account.accountId, props.refresh]);

  async function purchase(productId: (typeof MOBILE_STORE_SUBSCRIPTION_IDS)[number]) {
    setBusy(true);
    try {
      const results = await purchaseMobileStoreMembership(props.account.accountId, props.appAccountToken, productId, qualificationAccount);
      if (results.length === 0) {
        setMessage(`No ${storeName} purchase was completed.`);
        return;
      }
      const pending = results.some(({ status }) => status === "PENDING");
      setMessage(pending ? `${storeName} is processing this purchase. Access changes only after account verification.` : `${storeName} verified the purchase with Portability. Refreshing your membership…`);
      await props.refresh();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : `The ${storeName} purchase could not be completed`);
    } finally {
      setBusy(false);
    }
  }

  async function restore() {
    setBusy(true);
    try {
      const results = await restoreMobileStorePurchases(props.account.accountId, props.appAccountToken, qualificationAccount);
      setMessage(results.length === 0 ? `${storeName} found no restorable Portability memberships.` : "Restored purchases were sent to Portability for verification.");
      await props.refresh();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : `${storeName} purchases could not be restored`);
    } finally {
      setBusy(false);
    }
  }

  async function retryMembershipStatus() {
    setBusy(true);
    setMessage(`Checking ${storeName}…`);
    try {
      await props.refresh();
      setMessage(`Membership status checked. ${storeName} is still unavailable on this device. No purchase was started.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : `The ${storeName} membership status could not be checked`);
    } finally {
      setBusy(false);
    }
  }

  if (!available) return <section className="section card" aria-labelledby="mobile-store-membership-title">
    <div className="section-heading"><div><span className="eyebrow">{storeName.toUpperCase()}</span><h3 id="mobile-store-membership-title">{copy("mobile.membership")}</h3></div><p>Current plan: {membershipTierLabel(props.account.tier)}</p></div>
    <p className="notice" aria-live="polite">Subscriptions aren’t available on this device right now. Please try again shortly.</p>
    <p className="notice" aria-live="polite">{message}</p>
    <p>Your current account remains {membershipTierLabel(props.account.tier)}. No purchase has been started.</p>
    <div className="button-row">
      <button className="secondary" type="button" disabled={busy} onClick={() => void retryMembershipStatus()}>{busy ? `Checking ${storeName}…` : `Check ${storeName} again`}</button>
      <button className="secondary" type="button" onClick={() => void openMobileStoreSubscriptionManagement().catch((error: unknown) => setMessage(error instanceof Error ? error.message : `${storeName} subscription management could not be opened`))}>Manage in {storeName}</button>
    </div>
    <p className="microcopy">No purchase was started. Subscriptions renew automatically only after the store presents and confirms a real product purchase. <a href="https://portabilityapp.net/terms" target="_blank" rel="noreferrer">Terms of Use</a> · <a href="https://portabilityapp.net/privacy" target="_blank" rel="noreferrer">Privacy Policy</a></p>
  </section>;
  return <section className="section card" aria-labelledby="mobile-store-membership-title">
    <div className="section-heading"><div><span className="eyebrow">{storeName.toUpperCase()}</span><h3 id="mobile-store-membership-title">{copy("mobile.membership")}</h3></div><p>Monthly renewal. No trial or promotional offer.</p></div>
    <p className="notice" aria-live="polite">{message}</p>
    <div className="grid">{products.map((product) => <article className="card" key={product.id}><h3>{product.name}</h3><p>{product.description}</p><p><strong>{product.formattedPrice ?? "Price unavailable"}</strong>{product.billingPeriod === "P1M" ? " per month" : ""}</p><button className="primary" type="button" disabled={busy} onClick={() => void purchase(product.id as (typeof MOBILE_STORE_SUBSCRIPTION_IDS)[number])}>Choose {product.name}</button></article>)}</div>
    <div className="button-row"><button className="secondary" type="button" disabled={busy} onClick={() => void restore()}>Restore purchases</button><button className="secondary" type="button" disabled={busy} onClick={() => void openMobileStoreSubscriptionManagement().catch((error: unknown) => setMessage(error instanceof Error ? error.message : `${storeName} subscription management could not be opened`))}>Manage in {storeName}</button></div>
    <p className="microcopy">Subscriptions renew automatically each month until canceled in {storeName}. The store shows the final localized price before you confirm. You can manage or cancel from your store account.</p>
    <p className="microcopy">{storeName} verifies the purchase, then Portability updates membership access on your account. <a href="https://portabilityapp.net/terms" target="_blank" rel="noreferrer">Terms of Use</a> · <a href="https://portabilityapp.net/privacy" target="_blank" rel="noreferrer">Privacy Policy</a></p>
  </section>;
}

function Profile(props: { readonly bootstrap: Bootstrap; readonly liveServicesAvailable: boolean; readonly preferences: MobilePreferences; readonly onPreferencesChange: (preferences: MobilePreferences) => void; readonly nativeSecurity: NativeSecurityStatus | null; readonly biometricLockAuthenticated: boolean; readonly onAuthenticateDeviceOwner: () => void; readonly onOpenDeviceSettings: () => void; readonly refresh: () => Promise<void> }) {
  const copy = (key: PortabilityMessageKey) => localizedMessage(props.preferences.locale, key);
  const [profileSection, setProfileSection] = useState<ProfileSection>("overview");
  const [message, setMessage] = useState<string | null>(null);
  const [parentSessionId, setParentSessionId] = useState<string | null>(null);
  const profileRootRef = useRef<HTMLElement | null>(null);
  function selectProfileSection(section: ProfileSection) {
    setProfileSection(section);
    window.requestAnimationFrame(() => {
      profileRootRef.current?.scrollIntoView({ block: "start" });
      profileRootRef.current?.focus({ preventScroll: true });
    });
  }
  async function createSupport(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    try {
      const result = await apiRequest<{ readonly caseId: string }>("/v1/support-cases", { method: "POST", qualificationAccount, body: { category: String(data.get("category")), summary: String(data.get("summary")), replyEmail: String(data.get("replyEmail")) } });
      void result.caseId;
      setMessage("Your support request was received. We’ll email you when there is an update.");
      event.currentTarget.reset();
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "Support case could not be created");
    }
  }
  async function publishMemberPage(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    try {
      const page = await apiRequest<{ readonly handle: string }>("/v1/member-page", {
        method: "PUT",
        qualificationAccount,
        body: { handle: String(data.get("handle")), displayName: String(data.get("displayName")), bio: String(data.get("bio")), publicVisible: data.get("publicVisible") === "on", searchIndexOptIn: data.get("searchIndexOptIn") === "on" }
      });
      setMessage(`Member page saved. Your permanent handle is ${page.handle}.`);
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "Member page could not be saved");
    }
  }
  async function deleteAccount() {
    const subscriptionStoreName = mobileStoreBillingAvailable() ? mobileStoreName() : "the App Store or Google Play";
    const paidAccountWarning = props.bootstrap.entitlement?.tier === "FREE" ? "" : `\n\nDeleting this account does not cancel any active ${subscriptionStoreName} subscription. Manage or cancel the subscription in your store account before deleting if you do not want future renewals.`;
    if (!globalThis.confirm(`Delete this Portability account? Access and future commission eligibility end immediately. Legally required financial, tax, fraud, dispute, and compliance records are retained.${paidAccountWarning}`)) return;
    try {
      await apiRequest("/v1/account/deletion", { method: "POST", qualificationAccount });
      setMessage("Account deletion accepted. Active sessions and non-retained profile data are being removed.");
      await props.refresh();
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "Account deletion could not be requested");
    }
  }
  async function signOut() {
    try {
      await apiRequest("/v1/auth/signout", { method: "POST" });
      setMessage("You are signed out. Browse mode remains available.");
      await props.refresh();
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "Sign-out could not be completed");
    }
  }
  const profileSettings = <ProfileSettings
    preferences={props.preferences}
    onChange={props.onPreferencesChange}
    {...(props.nativeSecurity === null ? {} : { permissions: props.nativeSecurity.permissions })}
    biometricAvailable={props.nativeSecurity?.biometric.biometricAvailable === true}
    biometricLockAuthenticated={props.biometricLockAuthenticated}
    onAuthenticateDeviceOwner={props.onAuthenticateDeviceOwner}
    {...(props.nativeSecurity?.appSettingsAvailable === true ? { onOpenDeviceSettings: props.onOpenDeviceSettings } : {})}
  />;
  if (props.bootstrap.account === null) return <section className="grid"><article className="card"><h2>{copy("auth.continueWithoutAccount")}</h2><p>Travel discovery stays open. Sign in when you want cloud trips, bookings, saved items, rewards, Creator tools, chat, or Kid's Corner history.</p><p className="notice">Sign-in options appear when they are available on this device.</p></article><AuthenticationControls liveServicesAvailable={props.liveServicesAvailable} locale={props.preferences.locale} /><article className="card"><h2>{copy("auth.freeForever")}</h2><p>Free includes travel help, booking handoff when a source is active, saved trips, and basic offline essentials. There is no trial timer.</p></article>{profileSettings}</section>;
  const entitlement = props.bootstrap.entitlement!;
  const sectionLabels: readonly { readonly id: ProfileSection; readonly label: string; readonly summary: string }[] = [
    { id: "overview", label: "Overview", summary: "Membership, wallet, earnings, and quick actions." },
    { id: "account", label: "Account", summary: "Sign out, public page, and account deletion." },
    { id: "membership", label: "Membership", summary: "Plans, store billing, and purchase restoration." },
    { id: "rewards", label: "Rewards", summary: "Referrals, points, payouts, and commerce history." },
    { id: "creator", label: "Creator", summary: "Creator tools, Live setup, pages, campaigns, and moderation." },
    { id: "family", label: "Family", summary: "Kid's Corner and family controls." },
    { id: "preferences", label: "Preferences", summary: "Language, accessibility, device permissions, and security." },
    { id: "support", label: "Support", summary: "Help, safety, appeals, and legal account actions." }
  ];
  const SectionButton = (section: (typeof sectionLabels)[number]) => <button
    key={section.id}
    className={profileSection === section.id ? "primary" : "secondary"}
    type="button"
    aria-controls={`profile-section-${section.id}`}
    aria-current={profileSection === section.id ? "page" : undefined}
    onClick={() => selectProfileSection(section.id)}
  ><span>{section.label}</span></button>;
  const selectedSection = sectionLabels.find(({ id }) => id === profileSection)!;
  return <section ref={profileRootRef} tabIndex={-1} aria-labelledby="profile-title"><div className="section-heading"><div><span className="eyebrow">{copy("mobile.profileTitle").toUpperCase()}</span><h2 id="profile-title">{props.bootstrap.account.displayName}</h2></div><span className="badge">{membershipTierLabel(entitlement.tier)}</span></div>
    <nav className="profile-section-nav" aria-label="Profile sections">{profileSection === "overview"
      ? <div className="profile-section-menu">{sectionLabels.filter(({ id }) => id !== "overview").map(SectionButton)}</div>
      : <div className="profile-section-detail-nav"><button className="secondary compact" type="button" aria-controls="profile-section-overview" onClick={() => selectProfileSection("overview")}>← Back to Profile</button><strong>{selectedSection.label}</strong></div>}
    </nav>
    {message && <p className="notice" aria-live="polite">{message}</p>}
    <section id="profile-section-overview" hidden={profileSection !== "overview"} className="section grid">
      <article className="card"><h3>{copy("mobile.membership")}</h3><p>{membershipTierLabel(entitlement.tier)} account. Travel help is included. Kid's Corner downloads: {entitlement.kidsCornerIncludedDownloadsPerMonth}/month.</p><button className="secondary compact" type="button" onClick={() => selectProfileSection("membership")}>Manage membership</button></article>
      <article className="card"><h3>{copy("mobile.walletPayouts")}</h3><p>No wallet, balance, or payout appears until a verified payout method exists.</p><button className="secondary compact" type="button" onClick={() => selectProfileSection("rewards")}>Open rewards</button></article>
      <article className="card"><h3>{copy("mobile.earnings")}</h3><p>{entitlement.minimumPayoutMinorUsd === null ? "Free accounts may refer but do not earn commissions." : "Earnings are paid after payout, tax, and account checks are current."}</p></article>
    </section>
    <section id="profile-section-account" hidden={profileSection !== "account"} className="section grid">
      <article className="card"><h3>{copy("mobile.accountAccess")}</h3><p>Sign out without losing browse mode.</p><button className="secondary" type="button" onClick={() => void signOut()}>{copy("auth.signOut")}</button></article>
      <article className="card"><h3>{copy("mobile.publicMemberPage")}</h3><form className="form" onSubmit={publishMemberPage}><Field label="Permanent handle" name="handle" required /><Field label="Display name" name="displayName" required defaultValue={props.bootstrap.account.displayName} /><Field label="Bio" name="bio"><textarea name="bio" maxLength={500} /></Field><label><input name="publicVisible" type="checkbox" /> Publish approved fields</label><label><input name="searchIndexOptIn" type="checkbox" /> Allow search indexing</label><button className="secondary" type="submit">{copy("action.save")}</button></form></article>
      <article className="card"><h3>{copy("mobile.legalDelete")}</h3><p>Deletion is available after a recent sign-in. Required legal, financial, tax, fraud, dispute, and compliance records are retained.</p>{entitlement.tier !== "FREE" && <><p className="notice">If you have an active store subscription, deleting your Portability account will not cancel it. Cancel it in your store account first if you do not want future renewals.</p>{mobileStoreBillingAvailable() && <button className="secondary" type="button" onClick={() => void openMobileStoreSubscriptionManagement().catch((caught: unknown) => setMessage(caught instanceof Error ? caught.message : "Store subscription management could not be opened"))}>Manage subscription in {mobileStoreName()}</button>}</>}<button className="secondary" type="button" onClick={() => void deleteAccount()}>{copy("action.deleteAccount")}</button></article>
    </section>
    <section id="profile-section-membership" hidden={profileSection !== "membership"} className="section">
      <MobileStoreMembershipBilling account={props.bootstrap.account} appAccountToken={props.bootstrap.mobileStoreAccountToken} providerCapabilities={props.bootstrap.providerCapabilities} locale={props.preferences.locale} refresh={props.refresh} />
    </section>
    <section id="profile-section-rewards" hidden={profileSection !== "rewards"} className="section">
      <CommerceAccountSurface signedIn={props.bootstrap.account !== null} locale={props.preferences.locale} />
      <MemberTools bootstrap={props.bootstrap} locale={props.preferences.locale} focus="rewards" />
    </section>
    <section id="profile-section-creator" hidden={profileSection !== "creator"} className="section">
      <CommunityAccountSurface signedIn={props.bootstrap.account !== null} accountId={props.bootstrap.account.accountId} displayName={props.bootstrap.account.displayName} tier={props.bootstrap.account.tier} parentSessionId={parentSessionId} locale={props.preferences.locale} scope="creator" />
    </section>
    <section id="profile-section-family" hidden={profileSection !== "family"} className="section">
      <MemberTools bootstrap={props.bootstrap} locale={props.preferences.locale} focus="family" onParentSessionChange={setParentSessionId} />
    </section>
    <section id="profile-section-preferences" hidden={profileSection !== "preferences"} className="section">
      {profileSettings}
    </section>
    <section id="profile-section-support" hidden={profileSection !== "support"} className="section card"><h3>{copy("mobile.getHelp")}</h3><form className="form" onSubmit={createSupport}><Field label="Issue" name="category"><select name="category" defaultValue="OTHER_UNRESOLVED"><option value="BOOKING_OR_REFUND">Booking or refund</option><option value="PAYMENT_DISPUTE">Payment dispute</option><option value="PAYOUT">Payout</option><option value="CHILD_SAFETY">Child safety</option><option value="MODERATION">Moderation</option><option value="APPEAL">Appeal</option><option value="OTHER_UNRESOLVED">Other unresolved issue</option></select></Field><Field label="Reply email" name="replyEmail" type="email" required /><Field label="What happened?" name="summary"><textarea name="summary" required /></Field><button className="primary" type="submit">{copy("mobile.supportCase")}</button></form></section>
  </section>;
}

function App() {
  const [tab, setTab] = useState<Tab>("home");
  const [authResult, setAuthResult] = useState<string | null>(() => authResultWasAcknowledged() ? null : latestAuthResult);
  const [bootstrap, setBootstrap] = useState<Bootstrap | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [liveServicesAvailable, setLiveServicesAvailable] = useState(false);
  const [preferences, setPreferences] = useState<MobilePreferences>(() => loadMobilePreferences(durableStorage(), deviceLocales()));
  const [accountSyncMessage, setAccountSyncMessage] = useState<string | null>(null);
  const [nativeSecurity, setNativeSecurity] = useState<NativeSecurityStatus | null>(null);
  const [biometricLockAuthenticated, setBiometricLockAuthenticated] = useState(false);
  const [lockMessage, setLockMessage] = useState("Verify with your device to reopen Portability.");
  const storeEntitlementReconciliationInFlightRef = useRef(false);
  const copy = (key: PortabilityMessageKey) => localizedMessage(preferences.locale, key);
  const activeTitle = copy(`nav.${tab}` as PortabilityMessageKey);

  useEffect(() => {
    saveMobilePreferences(durableStorage(), preferences);
    applyMobilePreferences(document, preferences);
    if (bootstrap?.account !== null && bootstrap?.account !== undefined && liveServicesAvailable) void syncAccountPreferences(preferences);
  }, [preferences]);

  useEffect(() => {
    void readNativeSecurityStatus().then((status) => {
      setNativeSecurity(status);
      if (!status.nativeAvailable || !preferences.biometricLock) setBiometricLockAuthenticated(true);
    }).catch(() => setNativeSecurity(null));
  }, []);

  useEffect(() => {
    let remove: (() => Promise<void>) | undefined;
    void CapacitorApp.addListener("appStateChange", ({ isActive }) => {
      if (!isActive && preferences.biometricLock) {
        setBiometricLockAuthenticated(false);
        setLockMessage("Verify with your device to reopen Portability.");
      }
      if (isActive) void readNativeSecurityStatus().then(setNativeSecurity).catch(() => undefined);
      if (isActive) void reconcileSignedInMobileStoreEntitlements();
    }).then((listener) => { remove = () => listener.remove(); });
    return () => { void remove?.(); };
  }, [preferences.biometricLock, bootstrap?.account?.accountId, bootstrap?.mobileStoreAccountToken, liveServicesAvailable]);

  useEffect(() => {
    void reconcileSignedInMobileStoreEntitlements();
  }, [bootstrap?.account?.accountId, bootstrap?.mobileStoreAccountToken, liveServicesAvailable]);

  async function authenticateDeviceOwner() {
    const result = await authenticateDeviceOwnerForSessionLock("Unlock Portability");
    setBiometricLockAuthenticated(result.authenticated);
    setLockMessage(result.authenticated ? "Device owner verified." : "Portability remains locked. Try again or use the device passcode when offered.");
  }

  function updatePreferences(next: MobilePreferences) {
    setPreferences(next);
    if (!next.biometricLock) setBiometricLockAuthenticated(true);
  }

  async function syncAccountPreferences(nextPreferences: MobilePreferences) {
    try {
      await apiRequest("/v1/account/preferences", { method: "PUT", qualificationAccount, body: { preferences: nextPreferences } });
      setAccountSyncMessage(null);
    } catch {
      setAccountSyncMessage("Preferences saved on this device. Cloud preference sync will retry when the account service is reachable.");
    }
  }

  async function syncAccountExploreState(state: AccountExploreState) {
    if (bootstrap?.account === null || bootstrap?.account === undefined || !liveServicesAvailable) return;
    try {
      await apiRequest("/v1/account/explore-state", { method: "PUT", qualificationAccount, body: { exploreState: state } });
      setAccountSyncMessage("Saved places and recent searches synced.");
    } catch {
      setAccountSyncMessage("Saved places and recent searches are saved on this device. Cloud sync will retry later.");
    }
  }

  async function reconcileSignedInMobileStoreEntitlements() {
    if (storeEntitlementReconciliationInFlightRef.current || !liveServicesAvailable || bootstrap?.account === null || bootstrap?.account === undefined || !mobileStoreBillingAvailable()) return;
    storeEntitlementReconciliationInFlightRef.current = true;
    try {
      const results = await reconcileMobileStoreEntitlements(bootstrap.account.accountId, bootstrap.mobileStoreAccountToken, qualificationAccount);
      if (results.length > 0) {
        setAccountSyncMessage("Store membership status was refreshed from this device.");
        await refresh();
      }
    } catch {
      setAccountSyncMessage("Store membership refresh will retry when the app returns to the foreground.");
    } finally {
      storeEntitlementReconciliationInFlightRef.current = false;
    }
  }

  async function refresh() {
    try {
      setBootstrap(await apiRequest<Bootstrap>("/v1/bootstrap", { qualificationAccount }));
      setLiveServicesAvailable(true);
      setError(null);
    } catch {
      setLiveServicesAvailable(false);
      setError("Limited preview: live search, sign-in, cloud trips, timing checks, and bookings are not connected yet. Browse mode and saved offline essentials remain available.");
      setBootstrap((existing) => existing ?? { navigation: PRIMARY_NAVIGATION_FALLBACK, account: null, entitlement: null, mobileStoreAccountToken: null, tripCount: 0, providerCapabilities: [], productionFixturesVisible: false });
    }
  }
  useEffect(() => { void refresh(); }, []);
  useEffect(() => {
    const accountId = bootstrap?.account?.accountId;
    if (accountId === undefined || !liveServicesAvailable) return;
    let disposed = false;
    async function reconcileAccountState() {
      const storage = durableStorage();
      try {
        const localPreferences = loadMobilePreferences(storage, deviceLocales());
        const localExplore = readLocalAccountExploreState(storage);
        const [cloudPreferences, cloudExplore] = await Promise.all([
          apiRequest<CloudPreferencesEnvelope & { readonly reconciled: true }>("/v1/account/preferences", { method: "POST", qualificationAccount, body: { preferences: localPreferences } }),
          apiRequest<{ readonly exploreState: AccountExploreState; readonly updatedAt: string; readonly reconciled: true }>("/v1/account/explore-state", { method: "POST", qualificationAccount, body: { exploreState: localExplore } })
        ]);
        if (disposed) return;
        const nextPreferences = chooseAccountPreferences(localPreferences, cloudPreferences);
        const nextExplore = cloudExplore.exploreState;
        saveMobilePreferences(storage, nextPreferences);
        replaceExploreState(storage, nextExplore);
        setPreferences(nextPreferences);
        if (!disposed) setAccountSyncMessage(null);
      } catch {
        if (!disposed) setAccountSyncMessage("Account sync is unavailable. Device preferences and saved items remain available locally.");
      }
    }
    void reconcileAccountState();
    return () => { disposed = true; };
  }, [bootstrap?.account?.accountId, liveServicesAvailable]);
  useEffect(() => {
    const receive = (event: Event) => {
      setAuthResult((event as CustomEvent<string>).detail);
      void refresh();
    };
    globalThis.addEventListener(AUTH_RESULT_EVENT, receive);
    return () => globalThis.removeEventListener(AUTH_RESULT_EVENT, receive);
  }, []);

  const lockRequired = preferences.biometricLock && (nativeSecurity === null || nativeSecurity.nativeAvailable) && !biometricLockAuthenticated;
  if (lockRequired) return <main className="shell" id="content"><section className="section card" aria-labelledby="device-lock-title"><span className="eyebrow">DEVICE SECURITY</span><h1 id="device-lock-title">Portability is locked</h1><p>{lockMessage}</p><button className="primary" type="button" onClick={() => void authenticateDeviceOwner()}>Unlock with device security</button></section></main>;

  const signedIn = bootstrap?.account !== null && bootstrap?.account !== undefined;
  return <><a className="skip-link" href="#content">{copy("nav.skipToContent")}</a><div className="shell"><header className="topbar"><div><p className="brand">{copy("app.brand").toUpperCase()}</p><h1>{activeTitle}</h1></div><span className="badge">{signedIn ? `SIGNED IN · ${membershipTierLabel(bootstrap.account!.tier)}` : "BROWSE · NOT SIGNED IN"}</span></header><main id="content" tabIndex={-1}>{authResult && <div className="notice service-status" role="alert"><span>{authResult}</span><div className="button-row"><button className="secondary compact" type="button" onClick={() => setTab("profile")}>{signedIn ? "View profile" : "Try sign in again"}</button><button className="secondary compact" type="button" onClick={() => { acknowledgeAuthResult(); setAuthResult(null); }}>Dismiss</button></div></div>}{error && <div className="notice service-status" role="status"><span>{error}</span><button className="secondary compact" type="button" onClick={() => void refresh()}>{copy("action.retry")}</button></div>}{accountSyncMessage && <p className="microcopy" role="status">{accountSyncMessage}</p>}{bootstrap === null ? <p>{copy("state.loading")}</p> : tab === "home" ? <Home bootstrap={bootstrap} liveServicesAvailable={liveServicesAvailable} locale={preferences.locale} onNavigate={setTab} /> : tab === "explore" ? <Explore bootstrap={bootstrap} locale={preferences.locale} preferences={preferences} onExploreStateChange={(state) => void syncAccountExploreState(state)} /> : tab === "trips" ? <Trips bootstrap={bootstrap} liveServicesAvailable={liveServicesAvailable} locale={preferences.locale} onNavigate={setTab} refresh={refresh} /> : tab === "live" ? <Live bootstrap={bootstrap} locale={preferences.locale} /> : <Profile bootstrap={bootstrap} liveServicesAvailable={liveServicesAvailable} preferences={preferences} onPreferencesChange={updatePreferences} nativeSecurity={nativeSecurity} biometricLockAuthenticated={biometricLockAuthenticated} onAuthenticateDeviceOwner={() => void authenticateDeviceOwner()} onOpenDeviceSettings={() => void openNativeAppSettings()} refresh={refresh} />}</main><footer className="legal-links" aria-label="Legal and support"><a href="https://portabilityapp.net/privacy" target="_blank" rel="noreferrer">{copy("legal.privacy")}</a><a href="https://portabilityapp.net/terms" target="_blank" rel="noreferrer">{copy("legal.terms")}</a><a href="https://portabilityapp.net/support" target="_blank" rel="noreferrer">{copy("legal.support")}</a><a href="https://portabilityapp.net/account-deletion" target="_blank" rel="noreferrer">{copy("mobile.legalDelete")}</a></footer></div>{bootstrap && <nav className="tabbar" aria-label="Main navigation">{PRIMARY_NAVIGATION_FALLBACK.map((item) => <button className="tab" key={item.id} type="button" aria-current={tab === item.id ? "page" : undefined} onClick={() => setTab(item.id)}>{localizedMessage(preferences.locale, `nav.${item.id}` as PortabilityMessageKey)}</button>)}</nav>}</>;
}

const root = document.querySelector("#root");
if (root === null) throw new Error("Portability root element is missing");
createRoot(root).render(<StrictMode><App /></StrictMode>);
